/*==============================================================================
  
  ------------------------------------------------------------------------------

  # DEVELOPMENT INFO

  ### EVENT DELEGATION
  - We utilise the Dom Delegate library in `vendor.js` to better manage event
    delegation.
  - For more info:
    https://github.com/Financial-Times/ftdomdelegate

  ### FUNCTION NAMING
  - Module names are capitalised e.g. `HeaderMain`
  - Functions/methods within modules are camelCase and include an underscore 
    if they are a private method. E.g:
    `_privateFunction` vs. `publicFunction`


  ------------------------------------------------------------------------------

  # TABLE OF CONTENTS

  # UTILITIES
  - __defProp                    public field declaration
  - delegate_default             A global variable to tap into the Dom Delegate library
  - supportsAnimationsAPI        A variable to detect support for Web Animations API
  - Input Binding Manager        Keeps values in sync across various elements
  - Focus Visible Polyfill       Polyfill for focus:visible
  - Trigger Event Helpers        Helpers for triggering events
  - Shopify Event Helpers        Helper to recognise Shopify events in theme editor
  - Throttle & Debounce          Simple throttle and debounce utilities
  - Media Features Helper        Helpers to detect media features
  - DOM Helpers                  getSiblings and resolveAsyncIterator
  - Image Helpers                getSizedImageUrl, getMediaSrcset, getSupportedSizes, 
                                 imageLoaded
  - Currency                     formatMoney function
  - Product Loader               Fetch utility to getShopify products
  - Library Loader               Fetch utility to conditionally load scripts
  - QR Code                      QR Code loader for Shopify Gift Card template

  # ELEMENTS
  - Custom HTML Element          A baseline custom web component used as a basis for
                                 multiple custom elements
  - Loading Bar                  Simple loading bar component
  - Openable Element             Used for openable elements such as drawers, popovers, 
                                 modals, etc
  - Loader Button Mixin          Mixin used as prototype for many buttons
  - Loader Button                Used for add to cart and form submissions
  - Toggle Button                Used for toggling open/close various elements
  - Toggle Link                  Link variation of toggle button
  - Confirm Button               Provides confirmation alert to users

  - Get Sticky Header Offset     Utility to determine offset to sticky header
  - Collapsible Content          Collapsibles component
  - Scrollable Content           Adds scrollable class to elements
  - Tabs Nav                     Tabs component
  - Modal Content                Modal component
  - Popup Modal                  Popup Modal for newsletter
  - Safe Sticky                  Advanced sticky component that will scroll a
                                 sticky element even if it is taller than viewport
  - Scroll Shadow                Provide visual indicator to scrollable elements
  - PageDots                     Dot navigation component
  - Prev Next Buttons            Arrow button component used widely
  - Native Carousel              Carousel with autoplay functionality
  - Combo Box                    Custom select functionality which appears as a
                                 popover on mobile
  - Popover Content              Popover component generally used for mobile
  - Drawer Content               Drawer component used across the site
  - Country Selector             Utility to show provinces on Shopify address forms
  - Quantity Selector            Simple quantity selector component
  - Input Number                 Used in conjunction with quantity selector component
  - Price Range                  Used for price range filters
  - Product Facet                Handles filters, pagination, and sort by triggered
                                 functionality and rendering of collection and search
  - Facet Filters                Handles filter functionality
  - Sort By Popover              Handles sort by functionality
  - Page Pagination              Handles AJAX pagination

  - Announcement Bar             Announcement bar component
  - Header Main                  Main header component
  - Predictive Search Input      Event listener for predictive search input
  - Predictive Search Drawer     Drawer component for predictive search
  - Desktop Navigation           Dropdown and mega menu functionality
  - Mobile Navigation            Drawer for mobile navigation

  - Product Media                Handles product media functionality
  - External Video               External video component
  - Native Video                 Internal video 
  - Native Hero Video            Internal video with error handling for low
                                 iOS power mode.
  - Model Media                  3D Model component
  - Product Carousel             Product gallery
  - Product Carousel Controls    Product gallery thumbnails
  - Product Media                Handles all product media
  - Product Zoom Modal           Zoom modal

  - Product Meta                 Product meta component for price and SKU
  - Product Variants             Advanced product variants functionality
  - Product Payment Container    Buy buttons
  - Product Personalisation      Personalisation functionality
  - Product Cross Sell           Cross Sell functionality
  - Line Item Personalisation    Edit personalisation in cart line items
  - Product Form                 Handles Add to Cart functionality

  - Cart Count                   Updates cart count
  - Cart Note                    Updates and saves cart note
  - Line Item                    Handles loader for line item quantity changes
  - Line Item Quantity           Handles line item quantity changes
  - Free Shipping Bar            Shown in mini and main cart
  - Cart Notification            Unused cart notification message
  - Cart Drawer                  Mini Cart drawer
  - Shipping Estimator           Shipping Estimator for Cart page

  - Search Page                  Search Page functionality
  - Event Modal                  Used for events blog page
  - Product List                 Includes carousel functionality
  - Slideshow Component          Slideshow section
  - Hero Image                   Hero Image section

  # DOM READY
  - InputBindingManager          Instantiate InputBindingManager
  - Page Transitions             
  - Window Height                Get window height and store as CSS variable
  - RTE Utilities                Add wrappers to RTE tables and videos
  - Tabbing Focus Utility        Checks for tabbing

==============================================================================*/

  /*==============================================================================
    ## Utilities
  ==============================================================================*/

  var __defProp = Object.defineProperty;

  var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;

  var __publicField = (obj, key, value) => {
    __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
    return value;
  };


  /**
   * A simple default variable to hook into the Delegate library
   */
  var delegate_default = Delegate;


  /**
   * Simple way to detect AnimationsAPI. We have some non-important animations
   * that are best achieved in JS. This variable helps ensure the animations
   * don't run for older browsers.
   */
  var supportsAnimationsAPI = 'animate' in document.body;










  /**
   * =============================================================
   * ## INPUT BINDING MANAGER
   *    Used to keep values in sync across various elements. For example
   *    if using a product sticky form, we want both the main form and
   *    sticky form to be in sync depending on user choice.
   * =============================================================
   */

  var InputBindingManager = class {
    constructor() {
      this.delegateElement = new delegate_default(document.body);
      this.delegateElement.on("change", "[data-bind-value]", this._onValueChanged.bind(this));
    }

    _onValueChanged(event, target) {

      const boundElement = document.getElementById(target.getAttribute("data-bind-value"));
      if (boundElement) {
        if (target.tagName === "SELECT") {
          target = target.options[target.selectedIndex];
        }

        boundElement.innerHTML = target.hasAttribute("title") ? target.getAttribute("title") : target.value;
      }
    }
  };









  /**
   * =============================================================
   * ## Focus Visible Polyfill
   * =============================================================
   */

  // Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
  try {
    document.querySelector(":focus-visible");
  } catch(e) {
    focusVisiblePolyfill();
  }

  function focusVisiblePolyfill() {
    const navKeys = ['ARROWUP', 'ARROWDOWN', 'ARROWLEFT', 'ARROWRIGHT', 'TAB', 'ENTER', 'SPACE', 'ESCAPE', 'HOME', 'END', 'PAGEUP', 'PAGEDOWN']
    let currentFocusedElement = null;
    let mouseClick = null;

    window.addEventListener('keydown', (event) => {
      if(navKeys.includes(event.code.toUpperCase())) {
        mouseClick = false;
      }
    });

    window.addEventListener('mousedown', (event) => {
      mouseClick = true;
    });

    window.addEventListener('focus', () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focus-visible');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focus-visible');

    }, true);
  }










  /**
   * =============================================================
   * ## Trigger Event Helper
   * =============================================================
   */

  function triggerEvent(element, name, data = {}) {
    element.dispatchEvent(new CustomEvent(name, {
      bubbles: true,
      detail: data
    }));
  }

  function triggerNonBubblingEvent(element, name, data = {}) {
    element.dispatchEvent(new CustomEvent(name, {
      bubbles: false,
      detail: data
    }));
  }










  /**
   * =============================================================
   * ## filterShopifyEvent
   * A helper to recognise Shopify events in the Theme Editor
   * =============================================================
   */
  function filterShopifyEvent(event, domElement, callback) {
    let executeCallback = false;
    if (event.type.includes("shopify:section")) {
      if (domElement.hasAttribute("section") && domElement.getAttribute("section") === event.detail.sectionId) {
        executeCallback = true;
      }
    } else if (event.type.includes("shopify:block") && event.target === domElement) {
      executeCallback = true;
    }
    if (executeCallback) {
      callback(event);
    }
  }









  /**
   * =============================================================
   * ## Throttle & Debounce Helpers
   * =============================================================
   */

  function throttle(callback, delay3 = 15) {
    let throttleTimeout = null, storedEvent = null;

    const throttledEventHandler = (event) => {
      storedEvent = event;
      const shouldHandleEvent = !throttleTimeout;
      if (shouldHandleEvent) {
        callback(storedEvent);
        storedEvent = null;
        throttleTimeout = setTimeout(() => {
          throttleTimeout = null;
          if (storedEvent) {
            throttledEventHandler(storedEvent);
          }
        }, delay3);
      }
    };
    return throttledEventHandler;
  }


  function debounce(fn, delay3) {
    let timer = null;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        fn.apply(this, args);
      }, delay3);
    };
  }









  /**
   * =============================================================
   * ## Media Features Helper
   * =============================================================
   */

  var MediaFeatures = class {
    static prefersReducedMotion() {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }
    static supportsHover() {
      return window.matchMedia("(pointer: fine)").matches;
    }
  };










  /**
   * =============================================================
   * ## DOM helpers
   * 
   * 1. getSiblings
   * 2. resolveAsyncIterator
   * =============================================================
   */

  function getSiblings(element, filter, includeSelf = false) {
    let siblings = [];
    let currentElement = element;
    while (currentElement = currentElement.previousElementSibling) {
      if (!filter || currentElement.matches(filter)) {
        siblings.push(currentElement);
      }
    }
    if (includeSelf) {
      siblings.push(element);
    }
    currentElement = element;
    while (currentElement = currentElement.nextElementSibling) {
      if (!filter || currentElement.matches(filter)) {
        siblings.push(currentElement);
      }
    }
    return siblings;
  }

  async function resolveAsyncIterator(target) {
    const processedTarget = [];
    if (!(target != null && typeof target[Symbol.iterator] === "function")) {
      target = [target];
    }
    for (const targetItem of target) {
      if (typeof targetItem[Symbol.asyncIterator] === "function") {
        for await (const awaitTarget of targetItem) {
          processedTarget.push(awaitTarget);
        }
      } else {
        processedTarget.push(targetItem);
      }
    }
    return processedTarget;
  }










  /**
   * =============================================================
   * ## IMAGE HELPERS
   * =============================================================
   */

  function getSizedMediaUrl(media, size) {
    let src = typeof media === "string" ? media : media["preview_image"] ? media["preview_image"]["src"] : media["url"];
    if (size === null) {
      return src;
    }
    if (size === "master") {
      return src.replace(/http(s)?:/, "");
    }
    const match = src.match(/\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif)(\?v=\d+)?$/i);
    if (match) {
      const prefix = src.split(match[0]);
      const suffix = match[0];
      return (prefix[0] + "_" + size + suffix).replace(/http(s)?:/, "");
    } else {
      return null;
    }
  }

  function getMediaSrcset(media, sizeList) {
    let srcset = [], supportedSizes = typeof media === "string" ? sizeList : getSupportedSizes(media, sizeList);
    supportedSizes.forEach((supportedSize) => {
      srcset.push(`${getSizedMediaUrl(media, supportedSize + "x")} ${supportedSize}w`);
    });
    return srcset.join(",");
  }

  function getSupportedSizes(media, desiredSizes) {
    let supportedSizes = [], mediaWidth = media["preview_image"]["width"];
    desiredSizes.forEach((width) => {
      if (mediaWidth >= width) {
        supportedSizes.push(width);
      }
    });
    return supportedSizes;
  }

  function imageLoaded(image) {
    return new Promise((resolve) => {
      if (!image || image.tagName !== "IMG" || image.complete) {
        resolve();
      } else {
        image.onload = () => resolve();
      }
    });
  }










  /**
   * =============================================================
   * ## CURRENCY
   * =============================================================
   */
  function formatMoney(cents, format = "") {
    if (typeof cents === "string") {
      cents = cents.replace(".", "");
    }
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/, formatString = format || window.themeVariables.settings.moneyFormat;
    function defaultTo(value2, defaultValue) {
      return value2 == null || value2 !== value2 ? defaultValue : value2;
    }
    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultTo(precision, 2);
      thousands = defaultTo(thousands, ",");
      decimal = defaultTo(decimal, ".");
      if (isNaN(number) || number == null) {
        return 0;
      }
      number = (number / 100).toFixed(precision);
      let parts = number.split("."), dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands), centsAmount = parts[1] ? decimal + parts[1] : "";
      return dollarsAmount + centsAmount;
    }
    let value = "";
    switch (formatString.match(placeholderRegex)[1]) {
      case "amount":
        value = formatWithDelimiters(cents, 2);
        break;
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0);
        break;
      case "amount_with_space_separator":
        value = formatWithDelimiters(cents, 2, " ", ".");
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_with_apostrophe_separator":
        value = formatWithDelimiters(cents, 2, "'", ".");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ",", ".");
        break;
      case "amount_no_decimals_with_space_separator":
        value = formatWithDelimiters(cents, 0, " ");
        break;
      case "amount_no_decimals_with_apostrophe_separator":
        value = formatWithDelimiters(cents, 0, "'");
        break;
    }
    if (formatString.indexOf("with_comma_separator") !== -1) {
      return formatString.replace(placeholderRegex, value);
    } else {
      return formatString.replace(placeholderRegex, value);
    }
  }










  /**
   * =============================================================
   * ## PRODUCT LOADER
   * =============================================================
   */
  var ProductLoader = class {
    static load(productHandle) {
      if (!productHandle) {
        return;
      }

      if (this.loadedProducts[productHandle]) {
        return this.loadedProducts[productHandle];
      }

      this.loadedProducts[productHandle] = new Promise(async (resolve) => {
        const response = await fetch(`${window.themeVariables.routes.rootUrlWithoutSlash}/products/${productHandle}.js`);
        const responseAsJson = await response.json();
        resolve(responseAsJson);
      });

      return this.loadedProducts[productHandle];
    }
  };

  __publicField(ProductLoader, "loadedProducts", {});











  /**
   * =============================================================
   * ## LIBRARY LOADER
   * =============================================================
   */
  var LibraryLoader = class {
    static load(libraryName) {
      const STATUS_REQUESTED = "requested";
      const STATUS_LOADED = "loaded";
      const library = this.libraries[libraryName];

      if (!library) {
        return;
      }

      if (library.status === STATUS_REQUESTED) {
        return library.promise;
      }

      if (library.status === STATUS_LOADED) {
        return Promise.resolve();
      }

      let promise;
      if (library.type === "script") {
        promise = new Promise((resolve, reject) => {
          let tag = document.createElement("script");
          tag.id = library.tagId;
          tag.src = library.src;
          tag.onerror = reject;
          tag.onload = () => {
            library.status = STATUS_LOADED;
            resolve();
          };
          document.body.appendChild(tag);
        });
      } else {
        promise = new Promise((resolve, reject) => {
          let tag = document.createElement("link");
          tag.id = library.tagId;
          tag.href = library.src;
          tag.rel = "stylesheet";
          tag.type = "text/css";
          tag.onerror = reject;
          tag.onload = () => {
            library.status = STATUS_LOADED;
            resolve();
          };
          document.body.appendChild(tag);
        });
      }

      library.promise = promise;
      library.status = STATUS_REQUESTED;
      return promise;
    }
  };

  __publicField(LibraryLoader, "libraries", {
    qrCode: {
      tagId: "qrCode",
      src: window.themeVariables.libs.qrCode,
      type: "script"
    },
    modelViewerUiStyles: {
      tagId: "shopify-model-viewer-ui-styles",
      src: "https://cdn.shopify.com/shopifycloud/model-viewer-ui/assets/v1.0/model-viewer-ui.css",
      type: "link"
    }
  });










  /**
   * =============================================================
   * ## QR CODE
   * =============================================================
   */
  var QrCode = class extends HTMLElement {
    async connectedCallback() {
      await LibraryLoader.load("qrCode");
      new window.QRCode(this, {
        text: this.getAttribute("identifier"),
        width: 200,
        height: 200
      });
    }
  };

  window.customElements.define("qr-code", QrCode);











  /**
   * =============================================================
   * ## CustomHTMLElement
   * 
   * A custom implementation of HTMLElements which includes
   * 1. Variable to indicate a section has been reloaded in Shopify theme editor
   * 2. Storing rootDelegate and delegate of elements
   * 3. Trigger events to show and hide a loading bar
   * 4. A unique disconnectedCallback to disconnect the previous functions
   * =============================================================
   */

  var CustomHTMLElement = class extends HTMLElement {
    constructor() {
      super();

      this._hasSectionReloaded = false;

      if (Shopify.designMode) {
        this.rootDelegate.on("shopify:section:select", (event) => {
          const parentSection = this.closest(".shopify-section");
          if (event.target === parentSection && event.detail.load) {
            this._hasSectionReloaded = true;
          }
        });
      }
    }

    get rootDelegate() {
      return this._rootDelegate = this._rootDelegate || new delegate_default(document.documentElement);
    }

    get delegate() {
      return this._delegate = this._delegate || new delegate_default(this);
    }

    showLoadingBar() {
      triggerEvent(document.documentElement, "theme:loading:start");
    }

    hideLoadingBar() {
      triggerEvent(document.documentElement, "theme:loading:end");
    }

    disconnectedCallback() {
      this.delegate.destroy();
      this.rootDelegate.destroy();
      delete this._delegate;
      delete this._rootDelegate;
    }
  };










  /**
   * =============================================================
   * ## LoadingBar
   * =============================================================
   */

  var LoadingBar = class extends CustomHTMLElement {
    constructor() {
      super();
      this.rootDelegate.on("theme:loading:start", this.show.bind(this));
      this.rootDelegate.on("theme:loading:end", this.hide.bind(this));
      this.delegate.on("transitionend", this._onTransitionEnd.bind(this));
    }

    show() {
      this.classList.add("is-visible");
      this.style.transform = "scaleX(0.4)";
    }
    
    hide() {
      this.style.transform = "scaleX(1)";
      this.classList.add("is-finished");
    }

    _onTransitionEnd(event) {
      if (event.propertyName === "transform" && this.classList.contains("is-finished")) {
        this.classList.remove("is-visible");
        this.classList.remove("is-finished");
        this.style.transform = "scaleX(0)";
      }
    }
  };

  window.customElements.define("loading-bar", LoadingBar);











  /**
   * =============================================================
   * ## OPENABLE ELEMENT
   * =============================================================
   */

  var OpenableElement = class extends CustomHTMLElement {
    static get observedAttributes() {
      return ["open"];
    }

    constructor() {
      super();

      // Open or close based on theme editor events
      if (Shopify.designMode) {
        this.rootDelegate.on("shopify:section:select", (event) => filterShopifyEvent(event, this, () => this.open = true));
        this.rootDelegate.on("shopify:section:deselect", (event) => filterShopifyEvent(event, this, () => this.open = false));
      }

      // if append-body, clone to body, and remove from flow
      if (this.hasAttribute("append-body")) {
        const existingNode = document.getElementById(this.id);
        this.removeAttribute("append-body");

        if (existingNode && existingNode !== this) {
          existingNode.replaceWith(this.cloneNode(true));
          this.remove();
        } else {
          document.body.appendChild(this);
        }
      }
    }

    connectedCallback() {
      // Event handlers to close
      this.delegate.on("click", ".openable__overlay", () => this.open = false);
      this.delegate.on("click", '[data-action="close"]', (event) => {
        // ensure close event does not bubble up
        event.stopPropagation();
        this.open = false;
      });
    }

    get requiresLoading() {
      return this.hasAttribute("href");
    }

    get open() {
      return this.hasAttribute("open");
    }

    set open(value) {
      if (value) {
        (async () => {
          await this._load();
          this.clientWidth;
          this.setAttribute("open", "");
        })();
      } else {
        this.removeAttribute("open");
      }
    }

    get shouldTrapFocus() {
      return true;
    }

    get returnFocusOnDeactivate() {
      return !this.hasAttribute("return-focus") || this.getAttribute("return-focus") === "true";
    }

    get focusTrap() {
      return this._focusTrap = this._focusTrap || createFocusTrap(this, {
        fallbackFocus: this,
        initialFocus: this.hasAttribute("initial-focus-selector") ? this.getAttribute("initial-focus-selector") : void 0,
        clickOutsideDeactivates: (event) => !(event.target.hasAttribute("aria-controls") && event.target.getAttribute("aria-controls") === this.id),
        allowOutsideClick: (event) => event.target.hasAttribute("aria-controls") && event.target.getAttribute("aria-controls") === this.id,
        returnFocusOnDeactivate: this.returnFocusOnDeactivate,
        onDeactivate: () => this.open = false,
        preventScroll: true
      });
    }

    attributeChangedCallback(name, oldValue, newValue) {
      switch (name) {
        case "open":
          if (oldValue === null && newValue === "") {
            if (this.shouldTrapFocus) {
              setTimeout(() => this.focusTrap.activate(), 150);
            }
            triggerEvent(this, "openable-element:open");
          } else if (newValue === null) {
            if (this.shouldTrapFocus) {
              this.focusTrap.deactivate();
            }
            triggerEvent(this, "openable-element:close");
          }
      }
    }

    async _load() {
      if (!this.requiresLoading) {
        return;
      }

      triggerNonBubblingEvent(this, "openable-element:load:start");

      const response = await fetch(this.getAttribute("href"));

      const element = document.createElement("div");
      element.innerHTML = await response.text();

      this.innerHTML = element.querySelector(this.tagName.toLowerCase()).innerHTML;

      this.removeAttribute("href");

      triggerNonBubblingEvent(this, "openable-element:load:end");
    }
  };

  window.customElements.define("openable-element", OpenableElement);










  /**
   * =============================================================
   * ## LOADER BUTTON MIXIN
   * =============================================================
   */

  var LoaderButtonMixin = {
    _prepareButton() {
      this.originalContent = this.innerHTML;
      this._startTransitionPromise = null;

      this.innerHTML = `
      <span class="loader-button__text">${this.innerHTML}</span>
      <span class="loader-button__loader" hidden>
        <div class="spinner">
          <svg focusable="false" width="24" height="24" class="icon icon--spinner" viewBox="25 25 50 50">
            <circle cx="50" cy="50" r="20" fill="none" stroke="currentColor" stroke-width="5"></circle>
          </svg>
        </div>
      </span>
    `;
      this.textElement = this.firstElementChild;
      this.spinnerElement = this.lastElementChild;
      window.addEventListener("pagehide", () => this.removeAttribute("aria-busy"));
    },

    _startTransition() {
      const textAnimation = this.textElement.animate({
        opacity: [1, 0],
        transform: ["translateY(0)", "translateY(-10px)"]
      }, {
        duration: 75,
        easing: "ease",
        fill: "forwards"
      });

      this.spinnerElement.hidden = false;

      const spinnerAnimation = this.spinnerElement.animate({
        opacity: [0, 1],
        transform: ["translate(-50%, 0%)", "translate(-50%, -50%)"]
      }, {
        duration: 75,
        delay: 75,
        easing: "ease",
        fill: "forwards"
      });

      this._startTransitionPromise = Promise.all([

        new Promise((resolve) => textAnimation.onfinish = () => resolve()),
        new Promise((resolve) => spinnerAnimation.onfinish = () => resolve())
      ]);
    },

    async _endTransition() {
      if (!this._startTransitionPromise) {
        return;
      }

      await this._startTransitionPromise;
      this.spinnerElement.animate({
        opacity: [1, 0],
        transform: ["translate(-50%, -50%)", "translate(-50%, -100%)"]
      }, {
        duration: 75,
        delay: 100,
        easing: "ease",
        fill: "forwards"
      }).onfinish = () => this.spinnerElement.hidden = true;
      this.textElement.animate({
        opacity: [0, 1],
        transform: ["translateY(10px)", "translateY(0)"]
      }, {
        duration: 75,
        delay: 175,
        easing: "ease",
        fill: "forwards"
      });
      this._startTransitionPromise = null;
    }
  };









  /**
   * =============================================================
   * ## LOADER BUTTON
   * =============================================================
   */
  var LoaderButton = class extends HTMLButtonElement {
    static get observedAttributes() {
      return ["aria-busy"];
    }

    constructor() {
      super();

      this.addEventListener("click", (event) => {
        if (this.type === "submit" && this.form && this.form.checkValidity() && !this.form.hasAttribute("is")) {
          if (/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
            event.preventDefault();

            this.setAttribute("aria-busy", "true");
            setTimeout(() => this.form.submit(), 250);
          } else {
            this.setAttribute("aria-busy", "true");
          }
        }
      });
    }

    connectedCallback() {
      this._prepareButton();
    }

    disconnectedCallback() {
      this.innerHTML = this.originalContent;
    }

    attributeChangedCallback(property, oldValue, newValue) {
      if (property === "aria-busy" && supportsAnimationsAPI) {
        if (newValue === "true") {
          this._startTransition();
        } else {
          this._endTransition();
        }
      }
    }
  };

  Object.assign(LoaderButton.prototype, LoaderButtonMixin);

  window.customElements.define("loader-button", LoaderButton, { extends: "button" });











  /**
   * =============================================================
   * ## TOGGLE BUTTON
   * =============================================================
   */

  var ToggleButton = class extends HTMLButtonElement {
    static get observedAttributes() {
      return ["aria-expanded", "aria-busy"];
    }

    constructor() {
      super();

      if (this.hasAttribute("loader")) {
        this._prepareButton();
      }

      this.addEventListener("click", this._onButtonClick.bind(this));
      this.rootDelegate = new delegate_default(document.documentElement);
    }

    _onButtonClick() {
      this.isExpanded = !this.isExpanded;
      if (this.hasAttribute("data-media-id")) {
        this.controlledElement.setActiveMedia(this.getAttribute("data-media-id"));
      }
    }

    connectedCallback() {
      document.addEventListener("openable-element:close", (event) => {
        if (this.controlledElement === event.target) {
          this.isExpanded = false;
          event.stopPropagation();
        }
      });

      document.addEventListener("openable-element:open", (event) => {
        if (this.controlledElement === event.target) {
          this.isExpanded = true;
          event.stopPropagation();
        }
      });

      this.rootDelegate.on("openable-element:load:start", `#${this.getAttribute("aria-controls")}`, () => {
        if (this.classList.contains("button")) {
          this.setAttribute("aria-busy", "true");
        } else if (this.offsetParent !== null) {
          triggerEvent(document.documentElement, "theme:loading:start");
        }
      }, true);

      this.rootDelegate.on("openable-element:load:end", `#${this.getAttribute("aria-controls")}`, () => {
        if (this.classList.contains("button")) {
          this.removeAttribute("aria-busy");
        } else if (this.offsetParent !== null) {
          triggerEvent(document.documentElement, "theme:loading:end");
        }
      }, true);
    }

    disconnectedCallback() {
      this.rootDelegate.destroy();
    }

    get isExpanded() {
      return this.getAttribute("aria-expanded") === "true";
    }

    set isExpanded(value) {
      this.setAttribute("aria-expanded", value ? "true" : "false");
    }

    get controlledElement() {
      return document.getElementById(this.getAttribute("aria-controls"));
    }

    attributeChangedCallback(name, oldValue, newValue) {
      switch (name) {
        case "aria-expanded":
          if (oldValue === "false" && newValue === "true") {
            this.controlledElement.open = true;
          } else if (oldValue === "true" && newValue === "false") {
            this.controlledElement.open = false;
          }
          break;
        case "aria-busy":
          if (this.hasAttribute("loader")) {
            if (newValue === "true") {
              this._startTransition();
            } else {
              this._endTransition();
            }
          }
          break;
      }
    }
  };

  Object.assign(ToggleButton.prototype, LoaderButtonMixin);

  window.customElements.define("toggle-button", ToggleButton, { extends: "button" });










  /**
   * =============================================================
   * ## TOGGLE LINK
   * =============================================================
   */

  var ToggleLink = class extends HTMLAnchorElement {
    static get observedAttributes() {
      return ["aria-expanded"];
    }

    constructor() {
      super();
      this.addEventListener("click", (event) => {
        event.preventDefault();
        this.isExpanded = !this.isExpanded;
      });
      this.rootDelegate = new delegate_default(document.documentElement);
    }

    connectedCallback() {
      this.rootDelegate.on("openable-element:close", `#${this.getAttribute("aria-controls")}`, (event) => {
        if (this.controlledElement === event.target) {
          this.isExpanded = false;
        }
      }, true);
      this.rootDelegate.on("openable-element:open", `#${this.getAttribute("aria-controls")}`, (event) => {
        if (this.controlledElement === event.target) {
          this.isExpanded = true;
        }
      }, true);
    }

    disconnectedCallback() {
      this.rootDelegate.destroy();
    }

    get isExpanded() {
      return this.getAttribute("aria-expanded") === "true";
    }

    set isExpanded(value) {
      this.setAttribute("aria-expanded", value ? "true" : "false");
    }

    get controlledElement() {
      return document.querySelector(`#${this.getAttribute("aria-controls")}`);
    }

    attributeChangedCallback(name, oldValue, newValue) {
      switch (name) {
        case "aria-expanded":
          if (oldValue === "false" && newValue === "true") {
            this.controlledElement.open = true;
          } else if (oldValue === "true" && newValue === "false") {
            this.controlledElement.open = false;
          }
      }
    }
  };

  window.customElements.define("toggle-link", ToggleLink, { extends: "a" });










  /**
   * =============================================================
   * ## CONFIRM BUTTON
   * =============================================================
   */

  var ConfirmButton = class extends HTMLButtonElement {
    connectedCallback() {
      this.addEventListener("click", (event) => {
        if (!window.confirm(this.getAttribute("data-message") || "Are you sure you wish to do this?")) {
          event.preventDefault();
        }
      });
    }
  };

  window.customElements.define("confirm-button", ConfirmButton, { extends: "button" });











  /**
   * =============================================================
   * ## GET STICKY HEADER OFFSET
   * =============================================================
   */

  function getStickyHeaderOffset() {
    const documentStyles = getComputedStyle(document.documentElement);
    return parseInt(documentStyles.getPropertyValue("--header-height") || 0) * parseInt(documentStyles.getPropertyValue("--enable-sticky-header") || 0) + parseInt(documentStyles.getPropertyValue("--announcement-bar-height") || 0) * parseInt(documentStyles.getPropertyValue("--enable-sticky-announcement-bar") || 0);
  }










  /**
   * =============================================================
   * ## COLLAPSIBLE CONTENT
   * =============================================================
   */
  var CollapsibleContent = class extends OpenableElement {
    constructor() {
      super();
      this.ignoreNextTransition = this.open;
      this.addEventListener("shopify:block:select", () => this.open = true);
      this.addEventListener("shopify:block:deselect", () => this.open = false);
    }

    get animateItems() {
      return this.hasAttribute("animate-items");
    }

    attributeChangedCallback(name) {
      if (this.ignoreNextTransition) {
        return this.ignoreNextTransition = false;
      }

      switch (name) {
        case "open":
          if (!supportsAnimationsAPI) {
            triggerEvent(this, this.open ? "openable-element:open" : "openable-element:close");
            return;
          }

          this.style.overflow = "hidden";

          const keyframes = {
            height: ["0px", `${this.scrollHeight}px`],
            visibility: ["hidden", "visible"]
          };

          if (this.animateItems) {
            keyframes["opacity"] = this.open ? [0, 0] : [0, 1];
          }

          this.animate(keyframes, {
            duration: 500,
            direction: this.open ? "normal" : "reverse",
            easing: "cubic-bezier(0.75, 0, 0.175, 1)"
          }).onfinish = () => {
            this.style.overflow = this.open ? "visible" : "hidden";
          };

          if (this.animateItems && this.open) {
            this.animate({
              opacity: [0, 1],
              transform: ["translateY(10px)", "translateY(0)"]
            }, {
              duration: 250,
              delay: 250,
              easing: "cubic-bezier(0.75, 0, 0.175, 1)"
            });
          }

          triggerEvent(this, this.open ? "openable-element:open" : "openable-element:close");
      }
    }
  };

  window.customElements.define("collapsible-content", CollapsibleContent);










  /**
   * =============================================================
   * ## Scrollable Content
   * =============================================================
   */

  var ScrollableContent = class extends CustomHTMLElement {
    connectedCallback() {
      this._checkScrollability();

      window.addEventListener("resize", this._checkScrollability.bind(this));
    }

    _checkScrollability() {
      this.classList.toggle("is-scrollable", this.scrollWidth > this.offsetWidth);
    }
  };

  window.customElements.define("scrollable-content", ScrollableContent);










  /**
   * =============================================================
   * ## TABS NAV
   * =============================================================
   */

  var TabsNav = class extends HTMLElement {
    connectedCallback() {
      this.buttons = Array.from(this.querySelectorAll("button[aria-controls]"));
      this.scrollerElement = this.querySelector(".tabs-nav__scroller");

      this.buttonsLength = this.buttons.length - 1;

      this.buttons.forEach((button) => button.addEventListener("click", () => this.selectButton(button)));
      this.addEventListener("shopify:block:select", (event) => this.selectButton(event.target, !event.detail.load));

      window.addEventListener("resize", this._onWindowResized.bind(this));

      this._adjustNavigationPosition();

      if (this.hasArrows) {
        this.arrows = Array.from(this.querySelectorAll(".tabs-nav__arrow-item"));
        this._handleArrows();
        this._adjustActiveArrows();
      }
    }

    get hasArrows() {
      return this.hasAttribute("arrows");
    }

    get selectedTabIndex() {
      return this.buttons.findIndex((button) => button.getAttribute("aria-expanded") === "true");
    }

    get selectedButton() {
      return this.buttons.find((button) => button.getAttribute("aria-expanded") === "true");
    }

    selectButton(button, animate = true) {
      if (!this.buttons.includes(button) || this.selectedButton === button) {
        return;
      }

      const from = document.getElementById(this.selectedButton.getAttribute("aria-controls"));
      const to = document.getElementById(button.getAttribute("aria-controls"));

      if (animate && supportsAnimationsAPI) {
        this._transitionContent(from, to);
      } else {
        from.hidden = true;
        to.hidden = false;
      }

      this.selectedButton.setAttribute("aria-expanded", "false");
      button.setAttribute("aria-expanded", "true");

      triggerEvent(this, "tabs-nav:changed", { button });

      if (this.hasArrows) {
        this._adjustActiveArrows();
      }

      this._adjustNavigationPosition();
    }

    addButton(button) {
      button.addEventListener("click", () => this.selectButton(button));

      button.setAttribute("aria-expanded", "false");

      this.buttons[this.buttons.length - 1].insertAdjacentElement("afterend", button);
      this.buttons.push(button);

      this._adjustNavigationPosition(false);
    }

    _transitionContent(from, to) {
      from.animate({
        opacity: [1, 0]
      }, {
        duration: 250,
        easing: "ease"
      }).onfinish = () => {
        from.hidden = true;
        to.hidden = false;
        to.animate({
          opacity: [0, 1]
        }, {
          duration: 250,
          easing: "ease"
        });
      };
    }

    _onWindowResized() {
      this._adjustNavigationPosition();
    }

    _adjustNavigationPosition(shouldAnimate = true) {
      const windowHalfWidth = this.scrollerElement.clientWidth / 2;

      this.scrollerElement.scrollTo({
        behavior: shouldAnimate ? "smooth" : "auto",
        left: this.selectedButton.offsetLeft - windowHalfWidth + this.selectedButton.clientWidth / 2
      });
    }

    _adjustActiveArrows() {
      if (this.selectedTabIndex == 0) {
        this._resetActiveArrows();
        this.arrows[0].setAttribute("disabled", "disabled");
      } else if (this.selectedTabIndex == this.buttonsLength) {
        this._resetActiveArrows();
        this.arrows[1].setAttribute("disabled", "disabled");
      } else {
        this._resetActiveArrows();
      }
    }

    _resetActiveArrows() {
      this.arrows.forEach(arrow => {
        arrow.removeAttribute("disabled");
      });
    }

    _handleArrows() {
      this.arrows[0].addEventListener("click", () => {
        this.selectButton(this.buttons[Math.max(this.selectedTabIndex - 1, 0)]);
      });

      this.arrows[1].addEventListener("click", () => {
        this.selectButton(this.buttons[Math.min(this.selectedTabIndex + 1, this.buttons.length - 1)]);
      });
    }
  };

  window.customElements.define("tabs-nav", TabsNav);










  /**
   * =============================================================
   * ## MODAL CONTENT
   * =============================================================
   */
  var ModalContent = class extends OpenableElement {
    connectedCallback() {
      super.connectedCallback();

      this.delegate.on("click", ".modal__overlay", () => this.open = false);
    }

    attributeChangedCallback(name, oldValue, newValue) {
      super.attributeChangedCallback(name, oldValue, newValue);
      switch (name) {
        case "open":
          document.documentElement.classList.toggle("lock-all", this.open);
      }
    }
  };

  window.customElements.define("modal-content", ModalContent);










  /**
   * =============================================================
   * ## POPUP
   * =============================================================
   */

  var PopupModal = class extends ModalContent {
    connectedCallback() {
      super.connectedCallback();

      if (this.appearAfterDelay && !(this.onlyOnce && this.hasAppearedOnce)) {
        setTimeout(() => this.open = true, this.delay);
      }

      this.delegate.on("click", ".modal__overlay", () => this.open = false);
    }

    get appearAfterDelay() {
      return this.hasAttribute("delay");
    }

    get delay() {
      return parseInt(this.getAttribute("delay") || 0) * 1e3;
    }

    get onlyOnce() {
      return this.hasAttribute("only-once");
    }

    get hasAppearedOnce() {
      return localStorage.getItem("theme:popup-appeared") !== null;
    }

    attributeChangedCallback(name, oldValue, newValue) {
      super.attributeChangedCallback(name, oldValue, newValue);

      switch (name) {
        case "open":
          document.documentElement.classList.toggle("lock-all", this.open);
          if (this.open) {
            localStorage.setItem("theme:popup-appeared", true);
          }
      }
    }
  };

  window.customElements.define("popup-modal", PopupModal);










  /**
   * =============================================================
   * ## Safe Sticky
   * =============================================================
   */

  var SafeSticky = class extends HTMLElement {
    connectedCallback() {
      this.lastKnownY = window.scrollY;
      this.currentTop = 0;
      this.hasPendingRaf = false;
      window.addEventListener("scroll", this._checkPosition.bind(this));
    }

    get initialTopOffset() {
      return getStickyHeaderOffset() + (parseInt(this.getAttribute("offset")) || 0);
    }

    _checkPosition() {
      if (this.hasPendingRaf) {
        return;
      }

      this.hasPendingRaf = true;

      requestAnimationFrame(() => {
        let bounds = this.getBoundingClientRect();
        let maxTop = bounds.top + window.scrollY - this.offsetTop + this.initialTopOffset; 
        let minTop = this.clientHeight - window.innerHeight;

        if (window.scrollY < this.lastKnownY) {
          this.currentTop -= window.scrollY - this.lastKnownY;
        } else {
          this.currentTop += this.lastKnownY - window.scrollY;
        }
        this.currentTop = Math.min(Math.max(this.currentTop, -minTop), maxTop, this.initialTopOffset);
        this.lastKnownY = window.scrollY;
        this.style.top = `${this.currentTop}px`;
        this.hasPendingRaf = false;
      });
    }
  };

  window.customElements.define("safe-sticky", SafeSticky);









  /**
   * =============================================================
   * ## Scroll Shadow
   * =============================================================
   */
  var template = `
  <style>
    :host {
      display: inline-block;
      contain: layout;
      position: relative;
    }
    
    :host([hidden]) {
      display: none;
    }
    
    s {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      pointer-events: none;
      background-image:
        var(--scroll-shadow-top, radial-gradient(farthest-side at 50% 0%, rgba(0,0,0,.2), rgba(0,0,0,0))),
        var(--scroll-shadow-bottom, radial-gradient(farthest-side at 50% 100%, rgba(0,0,0,.2), rgba(0,0,0,0))),
        var(--scroll-shadow-left, radial-gradient(farthest-side at 0%, rgba(0,0,0,.2), rgba(0,0,0,0))),
        var(--scroll-shadow-right, radial-gradient(farthest-side at 100%, rgba(0,0,0,.2), rgba(0,0,0,0)));
      background-position: top, bottom, left, right;
      background-repeat: no-repeat;
      background-size: 100% var(--top, 0), 100% var(--bottom, 0), var(--left, 0) 100%, var(--right, 0) 100%;
    }
  </style>
  <slot></slot>
  <s></s>
  `;

  var Updater = class {
    constructor(targetElement) {
      this.scheduleUpdate = throttle(() => this.update(targetElement, getComputedStyle(targetElement)));
      this.resizeObserver = new ResizeObserver(this.scheduleUpdate.bind(this));
    }

    start(element) {
      if (this.element) {
        this.stop();
      }
      if (element) {
        element.addEventListener("scroll", this.scheduleUpdate);
        this.resizeObserver.observe(element);
        this.element = element;
      }
    }

    stop() {
      if (!this.element) {
        return;
      }
      this.element.removeEventListener("scroll", this.scheduleUpdate);
      this.resizeObserver.unobserve(this.element);
      this.element = null;
    }

    update(targetElement, style) {
      if (!this.element) {
        return;
      }
      const maxSize = style.getPropertyValue("--scroll-shadow-size") ? parseInt(style.getPropertyValue("--scroll-shadow-size")) : 0;
      const scroll = {
        top: Math.max(this.element.scrollTop, 0),
        bottom: Math.max(this.element.scrollHeight - this.element.offsetHeight - this.element.scrollTop, 0),
        left: Math.max(this.element.scrollLeft, 0),
        right: Math.max(this.element.scrollWidth - this.element.offsetWidth - this.element.scrollLeft, 0)
      };
      requestAnimationFrame(() => {
        for (const position of ["top", "bottom", "left", "right"]) {
          targetElement.style.setProperty(`--${position}`, `${scroll[position] > maxSize ? maxSize : scroll[position]}px`);
        }
      });
    }
  };

  var ScrollShadow = class extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: "open" }).innerHTML = template;
      this.updater = new Updater(this.shadowRoot.lastElementChild);
    }

    connectedCallback() {
      this.shadowRoot.querySelector("slot").addEventListener("slotchange", () => this.start());
      this.start();
    }

    disconnectedCallback() {
      this.updater.stop();
    }

    start() {
      this.updater.start(this.firstElementChild);
    }
  };

  if ("ResizeObserver" in window) {
    window.customElements.define("scroll-shadow", ScrollShadow);
  }










  /**
   * =============================================================
   * ## PAGE DOTS
   * =============================================================
   */

  var PageDots = class extends CustomHTMLElement {
    
    connectedCallback() {
      this.buttons = Array.from(this.querySelectorAll("button"));
      this.delegate.on("click", "button", (event, target) => {
        this._dispatchEvent(this.buttons.indexOf(target));
      });

      /**
       * This hooks into a CSS animation which triggers the page dot change,
       * and essentially allows us to use "autoplay" functionality on a 
       * carousel.
       */
      if (this.hasAttribute("animation-timer")) {
        this.delegate.on("animationend", (event) => {
          if (event.elapsedTime > 0) {
            this._dispatchEvent((this.selectedIndex + 1 + this.buttons.length) % this.buttons.length);
          }
        });
      }
    }

    get selectedIndex() {
      return this.buttons.findIndex((button) => button.getAttribute("aria-current") === "true");
    }

    set selectedIndex(selectedIndex) {
      this.buttons.forEach((button, index) => button.setAttribute("aria-current", selectedIndex === index ? "true" : "false"));
    }

    _dispatchEvent(index) {
      if (index !== this.selectedIndex) {
        this.dispatchEvent(new CustomEvent("page-dots:changed", {
          bubbles: true,
          detail: {
            index
          }
        }));
      }
    }
  };

  window.customElements.define("page-dots", PageDots);









  /**
   * =============================================================
   * ## PREV NEXT BUTTONS
   * =============================================================
   */
  var PrevNextButtons = class extends HTMLElement {
    connectedCallback() {
      this.prevButton = this.querySelector("button:first-of-type");
      this.nextButton = this.querySelector("button:last-of-type");

      this.prevButton.addEventListener("click", () => this.prevButton.dispatchEvent(new CustomEvent("prev-next:prev", { bubbles: true })));
      this.nextButton.addEventListener("click", () => this.nextButton.dispatchEvent(new CustomEvent("prev-next:next", { bubbles: true })));
    }

    set isPrevDisabled(value) {
      this.prevButton.disabled = value;
    }

    set isNextDisabled(value) {
      this.nextButton.disabled = value;
    }
  };

  var PrevButton = class extends HTMLButtonElement {
    connectedCallback() {
      this.addEventListener("click", () => this.dispatchEvent(new CustomEvent("prev-next:prev", { bubbles: true })));
    }
  };

  var NextButton = class extends HTMLButtonElement {
    connectedCallback() {
      this.addEventListener("click", () => this.dispatchEvent(new CustomEvent("prev-next:next", { bubbles: true })));
    }
  };

  window.customElements.define("prev-next-buttons", PrevNextButtons);
  window.customElements.define("prev-button", PrevButton, { extends: "button" });
  window.customElements.define("next-button", NextButton, { extends: "button" });










  /**
   * =============================================================
   * ## Native Carousel
   * =============================================================
   */

  var NativeCarousel = class extends CustomHTMLElement {

    connectedCallback() {
      this.items = Array.from(this.querySelectorAll("native-carousel-item"));
      this.pageDotsElements = Array.from(this.querySelectorAll("page-dots"));
      this.prevNextButtonsElements = Array.from(this.querySelectorAll("prev-next-buttons"));

      if (this.items.length > 1) {
        this.addEventListener("prev-next:prev", this.prev.bind(this));
        this.addEventListener("prev-next:next", this.next.bind(this));
        this.addEventListener("page-dots:changed", (event) => this.select(event.detail.index, true));

        if (Shopify.designMode) {
          this.addEventListener("shopify:block:deselect", this.startPlayer.bind(this));

          this.addEventListener("shopify:block:select", (event) => {
            this.pausePlayer();
            if (!(!event.detail.load && event.target.selected)) {
              this.select(event.target.index, !event.detail.load);
            }
          });
        }
      }

      const scrollerElement = this.items[0].parentElement;

      this.intersectionObserver = new IntersectionObserver(this._onVisibilityChanged.bind(this), { root: scrollerElement, rootMargin: `${scrollerElement.clientHeight}px 0px`, threshold: 0.8 });
      this.items.forEach((item) => this.intersectionObserver.observe(item));

      this.startPlayer();
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.intersectionObserver.disconnect();
    }

    get selectedIndex() {
      return this.items.findIndex((item) => item.selected);
    }

    prev(shouldAnimate = true) {
      this.select(Math.max(this.selectedIndex - 1, 0), shouldAnimate);
    }

    next(shouldAnimate = true) {
      this.select(Math.min(this.selectedIndex + 1, this.items.length - 1), shouldAnimate);
    }

    select(index, shouldAnimate = true) {
      const clampIndex = Math.max(0, Math.min(index, this.items.length));
      const selectedElement = this.items[clampIndex];

      this._adjustNavigationForElement(selectedElement);

      if (shouldAnimate) {
        this.items.forEach((item) => this.intersectionObserver.unobserve(item));

        setInterval(() => {
          this.items.forEach((item) => this.intersectionObserver.observe(item));
        }, 800);
      }

      this.items.forEach((item, loopIndex) => item.selected = loopIndex === clampIndex);

      const direction = 1;

      selectedElement.parentElement.scrollTo({ left: direction * (selectedElement.clientWidth * clampIndex), behavior: shouldAnimate ? "smooth" : "auto" });
    }

    /**
     * The autoplay functionality is triggered by events on the `PageDots` element
     */
    pausePlayer() {
      this.style.setProperty("--section-animation-play-state", "paused");
    }

    startPlayer() {
      if (this.hasAttribute("autoplay")) {
        this.style.setProperty("--section-animation-play-state", "running");
      }
    }

    _adjustNavigationForElement(selectedElement) {
      this.items.forEach((item) => item.selected = selectedElement === item);
      this.pageDotsElements.forEach((pageDot) => pageDot.selectedIndex = selectedElement.index);

      this.prevNextButtonsElements.forEach((prevNextButton) => {
        prevNextButton.isPrevDisabled = selectedElement.index === 0;
        prevNextButton.isNextDisabled = selectedElement.index === this.items.length - 1;
      });
    }

    _onVisibilityChanged(entries) {
      for (let entry of entries) {
        if (entry.isIntersecting) {
          this._adjustNavigationForElement(entry.target);
          break;
        }
      }
    }
  };

  var NativeCarouselItem = class extends CustomHTMLElement {
    static get observedAttributes() {
      return ["hidden"];
    }

    get index() {
      return [...this.parentNode.children].indexOf(this);
    }

    get selected() {
      return !this.hasAttribute("hidden");
    }

    set selected(value) {
      this.hidden = !value;
    }
  };

  window.customElements.define("native-carousel-item", NativeCarouselItem);
  window.customElements.define("native-carousel", NativeCarousel);









  /**
   * =============================================================
   * ## COMBO BOX
   * - Custom Select functionality which is styled like a popover on mobile
   * =============================================================
   */

  var ComboBox = class extends OpenableElement {
    connectedCallback() {
      super.connectedCallback();

      this.options = Array.from(this.querySelectorAll('[role="option"]'));

      this.delegate.on("click", '[role="option"]', this._onValueClicked.bind(this));
      this.delegate.on("keydown", '[role="listbox"]', this._onKeyDown.bind(this));
      this.delegate.on("change", "select", this._onValueChanged.bind(this));
      this.delegate.on("click", ".combo-box__overlay", () => this.open = false);

      // fit-toggle attribute used for sticky form option if we want in future
      if (this.hasAttribute("fit-toggle")) {
        const maxWidth = Math.max(...this.options.map((item) => item.clientWidth)), control = document.querySelector(`[aria-controls="${this.id}"]`);
        if (control) {
          control.style.setProperty("--largest-option-width", `${maxWidth + 2}px`);
        }
      }
    }

    get nativeSelect() {
      return this.querySelector("select");
    }

    set selectedValue(value) {
      this.options.forEach((option) => {
        option.setAttribute("aria-selected", option.getAttribute("value") === value ? "true" : "false");
      });
    }

    attributeChangedCallback(name, oldValue, newValue) {
      super.attributeChangedCallback(name, oldValue, newValue);

      switch (name) {
        case "open":
          if (this.open) {
            const boundingRect = this.getBoundingClientRect();
            this.classList.toggle("combo-box--top", boundingRect.top >= window.innerHeight / 2 * 1.5);
            setTimeout(() => this.focusTrap.activate(), 150);
          } else {
            this.focusTrap.deactivate();
            setTimeout(() => this.classList.remove("combo-box--top"), 200);
          }

          document.documentElement.classList.toggle("lock-mobile", this.open);
      }
    }

    _onValueClicked(event, target) {
      this.selectedValue = target.value;
      this.nativeSelect.value = target.value;
      this.nativeSelect.dispatchEvent(new Event("change", { bubbles: true }));
      this.open = false;
    }

    _onValueChanged(event, target) {
      Array.from(this.nativeSelect.options).forEach((option) => option.toggleAttribute("selected", target.value === option.value));
      this.selectedValue = target.value;
    }

    _onKeyDown(event) {
      var _a, _b;

      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();

        if (event.key === "ArrowDown") {
          (_a = document.activeElement.nextElementSibling) == null ? void 0 : _a.focus();
        } else {
          (_b = document.activeElement.previousElementSibling) == null ? void 0 : _b.focus();
        }
      }
    }
  };

  window.customElements.define("combo-box", ComboBox);











  /**
   * =============================================================
   * ## POPOVER CONTENT
   * =============================================================
   */
  var PopoverContent = class extends OpenableElement {
    connectedCallback() {
      super.connectedCallback();

      this.delegate.on("click", ".popover__overlay", () => this.open = false);
    }

    attributeChangedCallback(name, oldValue, newValue) {
      super.attributeChangedCallback(name, oldValue, newValue);
      switch (name) {
        case "open":
          document.documentElement.classList.toggle("lock-mobile", this.open);
      }
    }
  };
  
  window.customElements.define("popover-content", PopoverContent);











  /**
   * =============================================================
   * ## DRAWER CONTENT
   * =============================================================
   */

  var DrawerContent = class extends OpenableElement {
    connectedCallback() {
      super.connectedCallback();

      if (this.hasAttribute("reverse-breakpoint")) {
        this.originalDirection = this.classList.contains("drawer--from-left") ? "left" : "right";

        const matchMedia2 = window.matchMedia(this.getAttribute("reverse-breakpoint"));
        matchMedia2.addListener(this._checkReverseOpeningDirection.bind(this));

        this._checkReverseOpeningDirection(matchMedia2);
      }
      this.delegate.on("click", ".drawer__overlay", () => this.open = false);
    }

    attributeChangedCallback(name, oldValue, newValue) {
      super.attributeChangedCallback(name, oldValue, newValue);
      switch (name) {
        case "open":
          document.documentElement.classList.toggle("lock-all", this.open);
      }
    }

    _checkReverseOpeningDirection(match) {
      this.classList.remove("drawer--from-left");
      if (this.originalDirection === "left" && !match.matches || this.originalDirection !== "left" && match.matches) {
        this.classList.add("drawer--from-left");
      }
    }
  };

  window.customElements.define("drawer-content", DrawerContent);










  /**
   * =============================================================
   * ## COUNTRY SELECTOR
   * =============================================================
   */
  var CountrySelector = class extends HTMLSelectElement {
    connectedCallback() {
      this.provinceElement = document.getElementById(this.getAttribute("aria-owns"));

      /**
       * #EDIT: If the country selector is in the Shipping Estimator, we define
       * some additional variables so we can change the province selector.
       */
      this.inShippingEstimator = this.closest("shipping-estimator") ? true : void 0;

      if (this.inShippingEstimator) {
        this.countrySelectElement = document.getElementById("shipping-estimator[country]");
        this.provinceSelectElement = document.getElementById("shipping-estimator[province]");
      }

      this.addEventListener("change", this._updateProvinceVisibility.bind(this));

      if (this.hasAttribute("data-default")) {
        for (let i = 0; i !== this.options.length; ++i) {
          if (this.options[i].text === this.getAttribute("data-default")) {
            this.selectedIndex = i;
            break;
          }
        }
      }

      if (this.provinceElement.hasAttribute("data-default")) {
        for (let i = 0; i !== this.provinceElement.options.length; ++i) {
          if (this.provinceElement.options[i].text === this.provinceElement.getAttribute("data-default")) {
            this.provinceElement.selectedIndex = i;
            break;
          }
        }
      }
      this._updateProvinceVisibility();

      /**
       * #EDIT: If there isn't a default province for the user, and they have
       * selected the UK country, we want to set the province to England by 
       * default, instead of British Forces. This works on the Shipping Estimator
       * only.
       */
      if (this.inShippingEstimator && !this.provinceElement.hasAttribute("data-default") && this.countrySelectElement.value === 'United Kingdom') {
        this.provinceSelectElement.value = 'England';
      }
    }

    _updateProvinceVisibility() {
      const selectedOption = this.options[this.selectedIndex];

      if (!selectedOption) {
        return;
      }

      let provinces = JSON.parse(selectedOption.getAttribute("data-provinces") || "[]");
      let provinceSelectElement = this.provinceElement.tagName === "SELECT" ? this.provinceElement : this.provinceElement.querySelector("select");

      provinceSelectElement.innerHTML = "";

      if (provinces.length === 0) {
        this.provinceElement.hidden = true;
        return;
      }

      provinces.forEach((data) => {
        provinceSelectElement.options.add(new Option(data[1], data[0]));
      });

      this.provinceElement.hidden = false;
    }
  };

  window.customElements.define("country-selector", CountrySelector, { extends: "select" });









  /**
   * =============================================================
   * ## QUANTITY SELECTOR
   * =============================================================
   */
  var QuantitySelector = class extends CustomHTMLElement {
    connectedCallback() {
      this.inputElement = this.querySelector("input");

      this.delegate.on("click", "button:first-child", () => this.inputElement.quantity = this.inputElement.quantity - 1);
      this.delegate.on("click", "button:last-child", () => this.inputElement.quantity = this.inputElement.quantity + 1);
    }
  };

  window.customElements.define("quantity-selector", QuantitySelector);










  /**
   * =============================================================
   * ## INPUT NUMBER
   * =============================================================
   */
  var InputNumber = class extends HTMLInputElement {
    connectedCallback() {
      this.addEventListener("input", this._onValueInput.bind(this));
      this.addEventListener("change", this._onValueChanged.bind(this));
      this.addEventListener("keydown", this._onKeyDown.bind(this));
    }

    get quantity() {
      return parseInt(this.value);
    }

    set quantity(quantity) {
      const isNumeric = (typeof quantity === "number" || typeof quantity === "string" && quantity.trim() !== "") && !isNaN(quantity);

      if (quantity === "") {
        return;
      }

      if (!isNumeric || quantity < 0) {
        quantity = parseInt(quantity) || 1;
      }

      this.value = Math.max(this.min || 1, Math.min(quantity, this.max || Number.MAX_VALUE)).toString();
      this.size = Math.max(this.value.length + 1, 2);
    }

    _onValueInput() {
      this.quantity = this.value;
    }

    _onValueChanged() {
      if (this.value === "") {
        this.quantity = 1;
      }
    }

    _onKeyDown(event) {
      event.stopPropagation();

      if (event.key === "ArrowUp") {
        this.quantity = this.quantity + 1;
      } else if (event.key === "ArrowDown") {
        this.quantity = this.quantity - 1;
      }
    }
  };

  window.customElements.define("input-number", InputNumber, { extends: "input" });










  /**
   * =============================================================
   * ## PRICE RANGE
   * =============================================================
   */

  var PriceRange = class extends HTMLElement {
    connectedCallback() {
      this.rangeLowerBound = this.querySelector(".price-range__range-group input:first-child");
      this.rangeHigherBound = this.querySelector(".price-range__range-group input:last-child");
      
      this.textInputLowerBound = this.querySelector(".price-range__input:first-child input");
      this.textInputHigherBound = this.querySelector(".price-range__input:last-child input");

      this.textInputLowerBound.addEventListener("focus", () => this.textInputLowerBound.select());
      this.textInputHigherBound.addEventListener("focus", () => this.textInputHigherBound.select());

      this.textInputLowerBound.addEventListener("change", (event) => {
        event.target.value = Math.max(Math.min(parseInt(event.target.value), parseInt(this.textInputHigherBound.value || event.target.max) - 1), event.target.min);
        this.rangeLowerBound.value = event.target.value;
        this.rangeLowerBound.parentElement.style.setProperty("--range-min", `${parseInt(this.rangeLowerBound.value) / parseInt(this.rangeLowerBound.max) * 100}%`);
      });

      this.textInputHigherBound.addEventListener("change", (event) => {
        event.target.value = Math.min(Math.max(parseInt(event.target.value), parseInt(this.textInputLowerBound.value || event.target.min) + 1), event.target.max);
        this.rangeHigherBound.value = event.target.value;
        this.rangeHigherBound.parentElement.style.setProperty("--range-max", `${parseInt(this.rangeHigherBound.value) / parseInt(this.rangeHigherBound.max) * 100}%`);
      });

      this.rangeLowerBound.addEventListener("change", (event) => {
        this.textInputLowerBound.value = event.target.value;
        this.textInputLowerBound.dispatchEvent(new Event("change", { bubbles: true }));
      });

      this.rangeHigherBound.addEventListener("change", (event) => {
        this.textInputHigherBound.value = event.target.value;
        this.textInputHigherBound.dispatchEvent(new Event("change", { bubbles: true }));
      });

      this.rangeLowerBound.addEventListener("input", (event) => {
        triggerEvent(this, "facet:abort-loading");
        event.target.value = Math.min(parseInt(event.target.value), parseInt(this.textInputHigherBound.value || event.target.max) - 1);
        event.target.parentElement.style.setProperty("--range-min", `${parseInt(event.target.value) / parseInt(event.target.max) * 100}%`);
        this.textInputLowerBound.value = event.target.value;
      });

      this.rangeHigherBound.addEventListener("input", (event) => {
        triggerEvent(this, "facet:abort-loading");
        event.target.value = Math.max(parseInt(event.target.value), parseInt(this.textInputLowerBound.value || event.target.min) + 1);
        event.target.parentElement.style.setProperty("--range-max", `${parseInt(event.target.value) / parseInt(event.target.max) * 100}%`);
        this.textInputHigherBound.value = event.target.value;
      });
    }
  };

  window.customElements.define("price-range", PriceRange);










  /**
   * =============================================================
   * ## PRODUCT FACET
   * =============================================================
   */
  var ProductFacet = class extends CustomHTMLElement {
    connectedCallback() {
      this.delegate.on("pagination:page-changed", this._rerender.bind(this));
      this.delegate.on("facet:criteria-changed", this._rerender.bind(this));
      this.delegate.on("facet:abort-loading", this._abort.bind(this));
    }

    async _rerender(event) {
      history.replaceState({}, "", event.detail.url);
      this._abort();
      this.showLoadingBar();

      const url = new URL(window.location);
      url.searchParams.set("section_id", this.getAttribute("section-id"));

      try {
        this.abortController = new AbortController();

        const response = await fetch(url.toString(), { signal: this.abortController.signal });
        const responseAsText = await response.text();
        const fakeDiv = document.createElement("div");
        fakeDiv.innerHTML = responseAsText;

        this.querySelector("#facet-main").innerHTML = fakeDiv.querySelector("#facet-main").innerHTML;

        const activeFilterList = Array.from(fakeDiv.querySelectorAll(".product-facet__active-list")); 
        const toolbarItem = document.querySelector(".mobile-toolbar__item--filters");

        if (toolbarItem) {
          toolbarItem.classList.toggle("has-filters", activeFilterList.length > 0);
        }

        const filtersTempDiv = fakeDiv.querySelector("#facet-filters");

        if (filtersTempDiv) {
          const previousScrollTop = this.querySelector("#facet-filters .drawer__content").scrollTop;

          Array.from(this.querySelectorAll("#facet-filters-form .collapsible-toggle")).forEach((filterToggle) => {
            const filtersTempDivToggle = filtersTempDiv.querySelector(`[aria-controls="${filterToggle.getAttribute("aria-controls")}"]`);
            const isExpanded = filterToggle.getAttribute("aria-expanded") === "true";

            filtersTempDivToggle.setAttribute("aria-expanded", isExpanded ? "true" : "false");
            filtersTempDivToggle.nextElementSibling.toggleAttribute("open", isExpanded);
            filtersTempDivToggle.nextElementSibling.style.overflow = isExpanded ? "visible" : "";
          });

          this.querySelector("#facet-filters").innerHTML = filtersTempDiv.innerHTML;
          this.querySelector("#facet-filters .drawer__content").scrollTop = previousScrollTop;
        }

        const scrollTo = this.querySelector(".product-facet__meta-bar") || this.querySelector(".product-facet__product-list") || this.querySelector(".product-facet__main");

        requestAnimationFrame(() => {
          scrollTo.scrollIntoView({ block: "start", behavior: "smooth" });
        });

        this.hideLoadingBar();

      } catch (e) {
        if (e.name === "AbortError") {
          return;
        }
      }
    }

    _abort() {
      if (this.abortController) {
        this.abortController.abort();
      }
    }
  };
  window.customElements.define("product-facet", ProductFacet);










  /**
   * =============================================================
   * ## FACET FILTERS
   * =============================================================
   */
  var FacetFilters = class extends DrawerContent {
    connectedCallback() {
      super.connectedCallback();

      this.delegate.on("change", '[name^="filter."]', this._onFilterChanged.bind(this));
      this.rootDelegate.on("click", '[data-action="clear-filters"]', this._onFiltersCleared.bind(this));

      if (this.alwaysVisible) {
        this.matchMedia = window.matchMedia(window.themeVariables.breakpoints.mediumDown);
        this.matchMedia.addListener(this._adjustDrawer.bind(this));
        this._adjustDrawer(this.matchMedia);
      }
    }

    get alwaysVisible() {
      return this.hasAttribute("always-visible");
    }

    _onFiltersCleared(event, target) {
      event.preventDefault();
      triggerEvent(this, "facet:criteria-changed", { url: target.href });
    }

    _onFilterChanged() {
      const formData = new FormData(this.querySelector("#facet-filters-form"));
      const searchParamsAsString = new URLSearchParams(formData).toString();
      triggerEvent(this, "facet:criteria-changed", { url: `${window.location.pathname}?${searchParamsAsString}` });
    }

    _adjustDrawer(match) {
      this.classList.toggle("drawer", match.matches);
      this.classList.toggle("drawer--from-left", match.matches);
    }
  };

  window.customElements.define("facet-filters", FacetFilters);










  /**
   * =============================================================
   * ## SORT BY POPOVER
   * =============================================================
   */
  var SortByPopover = class extends PopoverContent {
    connectedCallback() {
      super.connectedCallback();
      this.delegate.on("change", '[name="sort_by"]', this._onSortChanged.bind(this));
    }

    _onSortChanged(event, target) {
      const currentUrl = new URL(location.href);
      currentUrl.searchParams.set("sort_by", target.value);
      currentUrl.searchParams.delete("page");
      this.open = false;
      this.dispatchEvent(new CustomEvent("facet:criteria-changed", {
        bubbles: true,
        detail: {
          url: currentUrl.toString()
        }
      }));
    }
  };

  window.customElements.define("sort-by-popover", SortByPopover);










  /**
   * =============================================================
   * ## PAGE PAGINATION
   * =============================================================
   */

  var PagePagination = class extends CustomHTMLElement {
    connectedCallback() {
      if (this.hasAttribute("ajax")) {
        this.delegate.on("click", "a", this._onLinkClicked.bind(this));
      }
    }
    _onLinkClicked(event, target) {
      event.preventDefault();

      const url = new URL(window.location.href);
      url.searchParams.set("page", target.getAttribute("data-page"));
      triggerEvent(this, "pagination:page-changed", { url: url.toString() });
    }
  };

  window.customElements.define("page-pagination", PagePagination);









  /**
   * =============================================================
   * ## ANNOUNCEMENT BAR
   * =============================================================
   */

  var AnnouncementBar = class extends CustomHTMLElement {
    connectedCallback() {
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(this._updateCssVariables.bind(this));
        this.resizeObserver.observe(this);
      }
    }

    _updateCssVariables(entries) {
      entries.forEach((entry) => {
        if (entry.target === this) {
          const height = entry.borderBoxSize ? entry.borderBoxSize.length > 0 ? entry.borderBoxSize[0].blockSize : entry.borderBoxSize.blockSize : entry.target.clientHeight;
          document.documentElement.style.setProperty("--announcement-bar-height", `${height}px`);
        }
      });
    }
  };
  window.customElements.define("announcement-bar", AnnouncementBar);










  /**
   * =============================================================
   * ## HEADER MAIN
   * =============================================================
   */

  var HeaderMain = class extends CustomHTMLElement {
    connectedCallback() {

      /**
       * We use store the header height in a CSS variable. This will update the 
       * CSS variables on window resizes.
       */
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(this._updateCssVariables.bind(this));
        this.resizeObserver.observe(this);
      }

      if (this.isTransparent) {
        this.isTransparencyDetectionLocked = false;

        this.delegate.on("desktop-nav:dropdown:open", () => this.lockTransparency = true);
        this.delegate.on("desktop-nav:dropdown:close", () => this.lockTransparency = false);

        this.rootDelegate.on("mobile-nav:open", () => this.lockTransparency = true);
        this.rootDelegate.on("mobile-nav:close", () => this.lockTransparency = false);

        /**
         * The true boolean on the below event listener will listen for events
         * during the capturing phase. Check the `useCapture` section on the Dom
         * Delegate documentation for further info:
         * https://github.com/Financial-Times/ftdomdelegate 
         */
        this.delegate.on("mouseenter", this._checkTransparentHeader.bind(this), true);
        this.delegate.on("mouseleave", this._checkTransparentHeader.bind(this));

        if (this.isSticky) {
          this._checkTransparentHeader();
          this._onWindowScrollListener = throttle(this._checkTransparentHeader.bind(this), 100);
          window.addEventListener("scroll", this._onWindowScrollListener);
        }
      }
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      
      if (window.ResizeObserver) {
        this.resizeObserver.disconnect();
      }

      if (this.isTransparent && this.isSticky) {
        window.removeEventListener("scroll", this._onWindowScrollListener);
      }
    }

    get isSticky() {
      return this.hasAttribute("sticky");
    }

    get isTransparent() {
      return this.hasAttribute("transparent");
    }

    get transparentHeaderThreshold() {
      return 25;
    }

    set lockTransparency(value) {
      this.isTransparencyDetectionLocked = value;
      this._checkTransparentHeader();
    }

    _updateCssVariables(entries) {
      entries.forEach((entry) => {
        if (entry.target === this) {
          const height = entry.borderBoxSize ? entry.borderBoxSize.length > 0 ? entry.borderBoxSize[0].blockSize : entry.borderBoxSize.blockSize : entry.target.clientHeight;

          document.documentElement.style.setProperty("--header-height", `${height}px`);
        }
      });
    }

    _checkTransparentHeader(event) {
      if (this.isTransparencyDetectionLocked || window.scrollY > this.transparentHeaderThreshold || event && event.type === "mouseenter") {
        this.classList.remove("header--transparent");
      } else {
        this.classList.add("header--transparent");
      }
    }

  }

  window.customElements.define("header-main", HeaderMain);










  /**
   * =============================================================
   * ## PREDICTIVE SEARCH INPUT
   * =============================================================
   */
  var PredictiveSearchInput = class extends HTMLInputElement {
    connectedCallback() {
      this.addEventListener("click", () => document.getElementById(this.getAttribute("aria-controls")).open = true);
    }
  };

  window.customElements.define("predictive-search-input", PredictiveSearchInput, { extends: "input" });











  /**
   * =============================================================
   * ## PREDICTIVE SEARCH DRAWER
   * =============================================================
   */
  var PredictiveSearchDrawer = class extends DrawerContent {
    connectedCallback() {
      super.connectedCallback();

      this.inputElement = this.querySelector('[name="q"]');
      this.drawerContentElement = this.querySelector(".drawer__content");
      this.drawerFooterElement = this.querySelector(".drawer__footer");
      this.loadingStateElement = this.querySelector(".predictive-search__loading-state");
      this.resultsElement = this.querySelector(".predictive-search__results");
      this.menuListElement = this.querySelector(".predictive-search__menu-list");

      this.delegate.on("input", '[name="q"]', this._debounce(this._onSearch.bind(this), 200));
      this.delegate.on("click", '[data-action="reset-search"]', this._startNewSearch.bind(this));
    }

    async _onSearch(event, target) {
      if (event.key === "Enter") {
        return;
      }

      if (this.abortController) {
        this.abortController.abort();
      }

      this.drawerContentElement.classList.remove("drawer__content--center");
      this.drawerFooterElement.hidden = true;

      if (target.value === "") {
        this.loadingStateElement.hidden = true;
        this.resultsElement.hidden = true;
        this.menuListElement ? this.menuListElement.hidden = false : "";
      } else {
        this.drawerContentElement.classList.add("drawer__content--center");
        this.loadingStateElement.hidden = false;
        this.resultsElement.hidden = true;
        this.menuListElement ? this.menuListElement.hidden = true : "";

        let searchResults = {};

        try {
          this.abortController = new AbortController();

          if (this._supportPredictiveApi()) {
            searchResults = await this._doPredictiveSearch(target.value);
          } else {
            searchResults = await this._doLiquidSearch(target.value);
          }
        } catch (e) {
          if (e.name === "AbortError") {
            return;
          }
        }

        this.loadingStateElement.hidden = true;
        this.resultsElement.hidden = false;
        this.menuListElement ? this.menuListElement.hidden = true : "";

        if (searchResults.hasResults) {
          this.drawerFooterElement.hidden = false;
          this.drawerContentElement.classList.remove("drawer__content--center");
        }

        this.resultsElement.innerHTML = searchResults.html;
      }
    }

    async _doPredictiveSearch(term) {
      const response = await fetch(`${window.themeVariables.routes.predictiveSearchUrl}?q=${term}&resources[limit]=10&resources[type]=${window.themeVariables.settings.searchMode}&resources[options[unavailable_products]]=${window.themeVariables.settings.searchUnavailableProducts}&resources[options[fields]]=title,body,product_type,variants.title,variants.sku,vendor&section_id=predictive-search`, {
        signal: this.abortController.signal
      });

      const div = document.createElement("div");
      div.innerHTML = await response.text();

      return { hasResults: div.querySelector(".predictive-search__results-categories") !== null, html: div.firstElementChild.innerHTML };
    }

    async _doLiquidSearch(term) {
      let promises = [], supportedTypes = window.themeVariables.settings.searchMode.split(",").filter((item) => item !== "collection");

      supportedTypes.forEach((searchType) => {
        promises.push(fetch(`${window.themeVariables.routes.searchUrl}?section_id=predictive-search-compatibility&q=${term}&type=${searchType}&options[unavailable_products]=${window.themeVariables.settings.searchUnavailableProducts}&options[prefix]=last`, {
          signal: this.abortController.signal
        }));
      });

      let results = await Promise.all(promises), resultsByCategories = {};

      for (const [index, value] of results.entries()) {
        const resultAsText = await value.text();
        const fakeDiv = document.createElement("div");

        fakeDiv.innerHTML = resultAsText;
        fakeDiv.innerHTML = fakeDiv.firstElementChild.innerHTML;

        if (fakeDiv.childElementCount > 0) {
          resultsByCategories[supportedTypes[index]] = fakeDiv.innerHTML;
        }
      }

      if (Object.keys(resultsByCategories).length > 0) {
        const entries = Object.entries(resultsByCategories), keys = Object.keys(resultsByCategories);
        let html = `
        <tabs-nav class="tabs-nav tabs-nav--edge2edge tabs-nav--narrow tabs-nav--no-border">
          <scrollable-content class="tabs-nav__scroller hide-scrollbar">
            <div class="tabs-nav__scroller-inner">
              <div class="tabs-nav__item-list">
      `;
        for (let [type, value] of entries) {
          html += `
          <button type="button" class="tabs-nav__item heading" aria-expanded="${type === keys[0] ? "true" : "false"}" aria-controls="predictive-search-${type}">
            ${window.themeVariables.strings["search" + type.charAt(0).toUpperCase() + type.slice(1) + "s"]}
          </button>
        `;
        }
        html += `
              </div>
            </div>
          </scrollable-content>
        </tabs-nav>
      `;
        html += '<div class="predictive-search__results-categories">';
        for (let [type, value] of entries) {
          html += `
          <div class="predictive-search__results-categories-item" ${type !== keys[0] ? "hidden" : ""} id="predictive-search-${type}">
            ${value}
          </div>
        `;
        }
        html += "</div>";
        return { hasResults: true, html };
      } else {
        return {
          hasResults: false,
          html: `
        <p class="">${window.themeVariables.strings.searchNoResults}</p>
          <div class="button-wrapper">
            <button type="button" data-action="reset-search" class="button button--primary">${window.themeVariables.strings.searchNewSearch}</button>
          </div>
        `
        };
      }
    }

    _startNewSearch() {
      this.inputElement.value = "";
      this.inputElement.focus();

      const event = new Event("input", {
        bubbles: true,
        cancelable: true
      });

      this.inputElement.dispatchEvent(event);
    }

    _supportPredictiveApi() {
      const shopifyFeatureRequests = JSON.parse(document.getElementById("shopify-features").innerHTML);
      return shopifyFeatureRequests["predictiveSearch"];
    }

    _debounce(fn, delay3) {
      let timer = null;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          fn.apply(this, args);
        }, delay3);
      };
    }
  };

  window.customElements.define("predictive-search-drawer", PredictiveSearchDrawer);










  /**
   * =============================================================
   * ## DESKTOP NAVIGATION
   * =============================================================
   */

  var DesktopNavigation = class extends CustomHTMLElement {
    connectedCallback() {
      this.openingTimeout = null;
      this.currentMegaMenu = null;

      this.delegate.on("mouseenter", ".has-dropdown", (event, target) => {
        if (event.target === target && event.relatedTarget !== null) {
          this.openDropdown(target);
        }
      }, true);

      /**
       * Click event handlers for touch screens 
       */
      this.delegate.on("click", ".header__linklist-link[aria-expanded], .nav-dropdown__link[aria-expanded]", (event, target) => {
        if (window.matchMedia("(hover: hover)").matches || target.getAttribute("aria-expanded") === "true") {
          return;
        }
        event.preventDefault();
        this.openDropdown(target.parentElement);
      });

      /**
       * Shopify theme editor events
       */
      this.delegate.on("shopify:block:select", (event) => this.openDropdown(event.target.parentElement));
      this.delegate.on("shopify:block:deselect", (event) => this.closeDropdown(event.target.parentElement));
    }

    openDropdown(parentElement) {
      const menuItem = parentElement.querySelector("[aria-controls]");
      const dropdown = parentElement.querySelector(`#${menuItem.getAttribute("aria-controls")}`);

      // Get current mega menu or set to null if it's a standard nav dropdown
      this.currentMegaMenu = dropdown.classList.contains("mega-menu") ? dropdown : null;

      let openingTimeout = setTimeout(() => {
        // If already open, then return
        if (menuItem.getAttribute("aria-expanded") === "true") {
          return;
        }

        // Set attributes for accessibility and CSS
        menuItem.setAttribute("aria-expanded", "true");
        dropdown.removeAttribute("hidden");

        const leaveListener = (event) => {
          if (event.relatedTarget !== null) {
            this.closeDropdown(parentElement);
            parentElement.removeEventListener("mouseleave", leaveListener);
          }
        };

        parentElement.addEventListener("mouseleave", leaveListener);
        openingTimeout = null;

        this.dispatchEvent(new CustomEvent("desktop-nav:dropdown:open", { bubbles: true }));
      }, 100);

      parentElement.addEventListener("mouseleave", () => {
        if (openingTimeout) {
          clearTimeout(openingTimeout);
        }
      }, { once: true });
    }

    closeDropdown(parentElement) {
      const menuItem = parentElement.querySelector("[aria-controls]");
      const dropdown = parentElement.querySelector(`#${menuItem.getAttribute("aria-controls")}`);

      requestAnimationFrame(() => {

        // Set attributes for CSS and accessibility
        menuItem.setAttribute("aria-expanded", "false");

        setTimeout(() => {
          dropdown.setAttribute("hidden", "");
          clearTimeout(this.openingTimeout);
        }, dropdown.classList.contains("mega-menu") && this.currentMegaMenu !== dropdown ? 250 : 0);

        this.dispatchEvent(new CustomEvent("desktop-nav:dropdown:close", { bubbles: true }));
      });
    }
  };

  window.customElements.define("desktop-navigation", DesktopNavigation);











  /**
   * =============================================================
   * ## MOBILE NAVIGATION
   * =============================================================
   */

  var MobileNavigation = class extends DrawerContent {
    attributeChangedCallback(name, oldValue, newValue) {
      super.attributeChangedCallback(name, oldValue, newValue);
      
      switch (name) {
        case "open":
          triggerEvent(this, this.open ? "mobile-nav:open" : "mobile-nav:close");
      }
    }
  };

  window.customElements.define("mobile-navigation", MobileNavigation);











  /**
   * =============================================================
   * ## EXTERNAL VIDEO
   * =============================================================
   */
  var ExternalVideo = class extends CustomHTMLElement {
    constructor() {
      super();

      this.hasLoaded = false;

      (async () => {
        if (this.autoPlay) {
          await this.untilVisible({ rootMargin: "300px", threshold: 0 });
          this.play();
        } else {
          this.addEventListener("click", this.play.bind(this), { once: true });
        }
      })();
    }

    untilVisible(intersectionObserverOptions = { rootMargin: "0px", threshold: 0.2 }) {
      const onBecameVisible = () => {
        this.classList.add("became-visible");
        this.style.opacity = "1";
      };
      return new Promise((resolve) => {
        if (window.IntersectionObserver) {
          this.intersectionObserver = new IntersectionObserver((event) => {
            if (event[0].isIntersecting) {
              this.intersectionObserver.disconnect();
              requestAnimationFrame(() => {
                resolve();
                onBecameVisible();
              });
            }
          }, intersectionObserverOptions);
          this.intersectionObserver.observe(this);
        } else {
          resolve();
          onBecameVisible();
        }
      });
    }

    get autoPlay() {
      return this.hasAttribute("autoplay");
    }

    get provider() {
      return this.getAttribute("provider");
    }

    async play() {
      if (!this.hasLoaded) {
        await this._setupPlayer();
      }

      if (this.provider === "youtube") {
        this.querySelector("iframe").contentWindow.postMessage(JSON.stringify({ event: "command", func: "playVideo", args: "" }), "*");
      } else if (this.provider === "vimeo") {
        this.querySelector("iframe").contentWindow.postMessage(JSON.stringify({ method: "play" }), "*");
      }
    }

    pause() {
      if (!this.hasLoaded) {
        return;
      }

      if (this.provider === "youtube") {
        this.querySelector("iframe").contentWindow.postMessage(JSON.stringify({ event: "command", func: "pauseVideo", args: "" }), "*");
      } else if (this.provider === "vimeo") {
        this.querySelector("iframe").contentWindow.postMessage(JSON.stringify({ method: "pause" }), "*");
      }
    }

    _setupPlayer() {
      if (this._setupPromise) {
        return this._setupPromise;
      }

      return this._setupPromise = new Promise((resolve) => {
        const template2 = this.querySelector("template");
        const node = template2.content.firstElementChild.cloneNode(true);

        node.onload = () => {
          this.hasLoaded = true;
          resolve();
        };

        if (this.autoPlay) {
          template2.replaceWith(node);
        } else {
          this.innerHTML = "";
          this.appendChild(node);
        }
      });
    }
  };

  window.customElements.define("external-video", ExternalVideo);










  /**
   * =============================================================
   * ## NATIVE VIDEO
   * =============================================================
   */

  var NativeVideo = class extends HTMLElement {
    constructor() {
      super();

      this.hasLoaded = false;

      if (this.autoPlay) {
        this.play();
      } else {
        this.addEventListener("click", this.play.bind(this), { once: true });
      }
    }

    get autoPlay() {
      return this.hasAttribute("autoplay");
    }

    play() {
      if (!this.hasLoaded) {
        this._replaceContent();
      }

      this.querySelector("video").play();
    }

    pause() {
      if (this.hasLoaded) {
        this.querySelector("video").pause();
      }
    }

    _replaceContent() {
      const node = this.querySelector("template").content.firstElementChild.cloneNode(true);
      this.innerHTML = "";
      this.appendChild(node);

      this.firstElementChild.addEventListener("play", () => {
        this.dispatchEvent(new CustomEvent("video:played", { bubbles: true }));
      });

      this.firstElementChild.addEventListener("pause", () => {
        this.dispatchEvent(new CustomEvent("video:paused", { bubbles: true }));
      });

      this.hasLoaded = true;
    }
  };

  window.customElements.define("native-video", NativeVideo);










  /**
   * =============================================================
   * ## NATIVE HERO VIDEO
   * A hero video will autoplay by default. We have some error handling for
   * iOS low power mode wherein we show a poster image instead.
   * =============================================================
   */

  var NativeHeroVideo = class extends HTMLElement {
    constructor() {
      super();

      this.hasLoaded = false;

      (async () => {
        await this.untilVisible({ rootMargin: "300px", threshold: 0 });
        this.play();
      })();
    }

    untilVisible(intersectionObserverOptions = { rootMargin: "0px", threshold: 0.2 }) {

      const onBecameVisible = () => {
        this.classList.add("became-visible");
        this.style.opacity = "1";
      };
      
      return new Promise((resolve) => {
        if (window.IntersectionObserver) {
          this.intersectionObserver = new IntersectionObserver((event) => {
            if (event[0].isIntersecting) {
              this.intersectionObserver.disconnect();
              requestAnimationFrame(() => {
                resolve();
                onBecameVisible();
              });
            }
          }, intersectionObserverOptions);
          this.intersectionObserver.observe(this);
        } else {
          resolve();
          onBecameVisible();
        }
      });
    }

    async play() {
      if (!this.hasLoaded) {
        this._replaceContent();
      }
    }

    _replaceContent() {
      const node = this.querySelector("template").content.firstElementChild.cloneNode(true);
      const posterNode = this.querySelector(".video-wrapper__poster").cloneNode(true);
      
      this.innerHTML = "";
      this.appendChild(node);

      this.querySelector("video").play()
      .then(() => {
        this.classList.add("loaded");
      })
      .catch((error) => {
        console.log(error);
        /**
         * Low power mode on iOS meaning the autoplay video is not played.
         * We replace content with video poster image instead.
         */
        // this.lastElementChild.remove();
        this.innerHTML = "";
        this.appendChild(posterNode);
      });

      this.firstElementChild.addEventListener("play", () => {
        this.dispatchEvent(new CustomEvent("video:played", { bubbles: true }));
      });

      this.hasLoaded = true;
    }
  };

  window.customElements.define("native-hero-video", NativeHeroVideo);










  /**
   * =============================================================
   * ## MODEL MEDIA
   * =============================================================
   */
  var ModelMedia = class extends HTMLElement {
    constructor() {
      super();

      LibraryLoader.load("modelViewerUiStyles");

      window.Shopify.loadFeatures([
        {
          name: "shopify-xr",
          version: "1.0",
          onLoad: this._setupShopifyXr.bind(this)
        },
        {
          name: "model-viewer-ui",
          version: "1.0",
          onLoad: () => {
            this.modelUi = new window.Shopify.ModelViewerUI(this.firstElementChild, { focusOnPlay: false });

            const modelViewer = this.querySelector("model-viewer");

            modelViewer.addEventListener("shopify_model_viewer_ui_toggle_play", () => {
              modelViewer.dispatchEvent(new CustomEvent("model:played", { bubbles: true }));
            });

            modelViewer.addEventListener("shopify_model_viewer_ui_toggle_pause", () => {
              modelViewer.dispatchEvent(new CustomEvent("model:paused", { bubbles: true }));
            });
          }
        }
      ]);
    }

    disconnectedCallback() {
      var _a;
      (_a = this.modelUi) == null ? void 0 : _a.destroy();
    }

    play() {
      if (this.modelUi) {
        this.modelUi.play();
      }
    }

    pause() {
      if (this.modelUi) {
        this.modelUi.pause();
      }
    }

    async _setupShopifyXr() {
      if (!window.ShopifyXR) {
        document.addEventListener("shopify_xr_initialized", this._setupShopifyXr.bind(this));
      } else {
        const product = await ProductLoader.load(this.getAttribute("product-handle"));
        const models = product["media"].filter((media) => media["media_type"] === "model");
        window.ShopifyXR.addModels(models);
        window.ShopifyXR.setupXRElements();
      }
    }
  };

  window.customElements.define("model-media", ModelMedia);










  /**
   * =============================================================
   * ## PRODUCT CAROUSEL 
   * =============================================================
   */

  var ProductCarousel = class extends CustomHTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll(".product__media-item"));

      if (this.items < 2) {
        return;
      }

      this.initPages();

      if (window.ResizeObserver) {
        // Resize event listener on the carousel
        const resizeObserver = new ResizeObserver(entries => {
          this.initPages();
        });

        resizeObserver.observe(this);
      }

      // A scroll event listener
      this.addEventListener("scroll", this.update.bind(this));
    }

    disconnectedCallback() {
      super.disconnectedCallback();
    }

    initPages() {
      // Defensive code to ensure slideshow items are larger than 0 width
      this.itemsToShow = Array.from(this.items).filter(element => element.clientWidth > 0);

      if (this.itemsToShow.length < 2) {
        return;
      }

      this.itemOffset = this.itemsToShow[1].offsetLeft - this.itemsToShow[0].offsetLeft;

      this.itemsPerPage = Math.floor((this.clientWidth - this.itemsToShow[0].offsetLeft) / this.itemOffset);

      this.totalPages = this.itemsToShow.length - this.itemsPerPage + 1;

      this.update();
    }

    update() {
      const previousPage = this.currentPage;

      // Get currentPage
      this.currentPage = Math.round(this.scrollLeft / this.itemOffset) + 1;

      // Checks for slide change and triggers event
      if (this.currentPage != previousPage) {
        triggerEvent(this, "product-carousel:slide-changed", { 
          currentPage: this.currentPage,
          selectedElement: this.itemsToShow[this.currentPage - 1]
        });
      }
    }

    resetPages() {
      this.items = this.querySelectorAll(".product__media-item");
      this.initPages();
    }
  };

  window.customElements.define("product-carousel", ProductCarousel);










  /**
   * =============================================================
   * ## PRODUCT CAROUSEL CONTROLS 
   * =============================================================
   */

  var ProductCarouselControls = class extends HTMLElement {
    connectedCallback() {}
  };

  window.customElements.define("product-carousel-controls", ProductCarouselControls);











  /**
   * =============================================================
   * ## PRODUCT MEDIA 
   * =============================================================
   */

  var ProductMedia = class extends CustomHTMLElement {
    connectedCallback() {
      var _a;

      this.productCarousel = this.querySelector("product-carousel");
      this.productThumbnails = this.querySelector("product-carousel-controls");

      /**
       * Click event listener for product thumbnails which will pass in the 
       * data-media-id 
       */
      this.productThumbnails.querySelectorAll('[data-action="select"]').forEach((mediaToSwitch) => {
        mediaToSwitch.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.mediaId, false));
      });

      if (this.productCarousel.childElementCount === 1) {
        return;
      }

      this.viewInSpaceElement = this.querySelector("[data-shopify-model3d-id]");

      this.selectedVariantMediaId = null;
      this.selectedElement = this.productCarousel.querySelector(".is-selected");

      // Check if product form with related form-id exists so we can listen for variant changes
      var form = document.getElementById(this.getAttribute("form-id"));
      (_a = document.getElementById(this.getAttribute("form-id"))) == null ? void 0 : _a.addEventListener("variant:changed", this._onVariantChanged.bind(this));

      this.productCarousel.addEventListener("product-carousel:slide-changed", debounce(this._onSlideChanged.bind(this), 500));

      this._onReady();
    }

    get thumbnailsPosition() {
      // If medium-down screen, then set thumbnails position as bottom, otherwise use section settings
      return window.matchMedia(window.themeVariables.breakpoints.mediumDown).matches ? "bottom" : this.getAttribute("thumbnails-position");
    }

    _onReady() {
      // Play autoplay video if it's the selected element
      if (["video", "external_video"].includes(this.selectedElement.getAttribute("data-media-type")) && this.hasAttribute("autoplay-video")) {
        this.selectedElement.firstElementChild.play();
      }
    }

    _onSlideChanged(event) {
      const mediaId = event.detail.selectedElement.dataset.mediaId;
      const activeMedia = this.productCarousel.querySelector(`[data-media-id="${ mediaId }"]`);
      const activeThumbnail = this.productThumbnails.querySelector(`[data-media-id="${mediaId}"]`);
      this.setActiveThumbnail(activeThumbnail);

      if (activeMedia.getAttribute("data-media-type") != "image") {
        this.checkMedia(activeMedia);
      }
    }

    setActiveThumbnail(activeThumbnail, animate = true) {
      if (!this.productThumbnails) {
        return;
      }

      this.productThumbnails.querySelectorAll('.product__thumbnail-item').forEach((element) => {
        element.removeAttribute("aria-current");
      });

      // Run scroll animation on thumbnails. This scrolls to the active thumbnail
      requestAnimationFrame(() => {
        if (activeThumbnail.offsetParent && activeThumbnail.offsetParent !== this) {
          const windowHalfHeight = activeThumbnail.offsetParent.clientHeight / 2, windowHalfWidth = activeThumbnail.offsetParent.clientWidth / 2;

          activeThumbnail.offsetParent.scrollTo({
            behavior: animate ? "smooth" : "auto",
            top: activeThumbnail.offsetTop - windowHalfHeight + activeThumbnail.clientHeight / 2,
            left: activeThumbnail.offsetLeft - windowHalfWidth + activeThumbnail.clientWidth / 2
          });
        }
      });

      activeThumbnail.setAttribute("aria-current", true);
    }


    setActiveMedia(mediaId, prepend, animate = true) {
      const activeMedia = this.productCarousel.querySelector(`[data-media-id="${ mediaId }"]`);

      // Pause any existing media and remove active class
      this.querySelectorAll('[data-media-id]').forEach((element) => {
        if (["external_video", "video", "model"].includes(element.getAttribute("data-media-type"))) {
          element.firstElementChild.pause();
        }

        element.classList.remove("is-selected");
      });

      // Add active class
      activeMedia.classList.add("is-selected");

      // On variant selection we prepend the image and thumbnail.
      // This is a recommended practice on the new Online Store 2.0 themese from
      // Shopify.
      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);

        if (this.productThumbnails) {
          const activeThumbnail = this.productThumbnails.querySelector(`[data-media-id="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }

        if (this.productCarousel) {
          this.productCarousel.resetPages();
        }
      }

      // Scrolls to the active media
      window.setTimeout(() => {
        activeMedia.parentElement.scrollTo({
          left: activeMedia.offsetLeft
        });
      });

      if (activeMedia.getAttribute("data-media-type") != "image") {
        this.checkMedia(activeMedia);
      }

      if (!this.productThumbnails) {
        return;
      }
      
      const activeThumbnail = this.productThumbnails.querySelector(`[data-media-id="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
    }

    checkMedia(activeMedia) {
      if (this.viewInSpaceElement) {
        this.viewInSpaceElement.setAttribute("data-shopify-model3d-id", this.viewInSpaceElement.getAttribute("data-shopify-model3d-default-id"));
      }

      // Play media if selected
      switch (activeMedia.getAttribute("data-media-type")) {
        case "model":
          this.viewInSpaceElement.setAttribute("data-shopify-model3d-id", activeMedia.getAttribute("data-media-id"));
          activeMedia.firstElementChild.play();
          break;
        case "external_video":
          if (this.hasAttribute("autoplay-video")) {
            activeMedia.firstElementChild.play();
          }
        case "video":
          if (this.hasAttribute("autoplay-video")) {
            activeMedia.firstElementChild.play();
          }
          break;
      }
    }

    _onVariantChanged(event) {
      const mediaId = event.detail.variant.featured_media.id;
      this.setActiveMedia(mediaId, true);
    }
  };

  window.customElements.define("product-media", ProductMedia);










  /**
   * =============================================================
   * ## PRODUCT ZOOM MODAL
   * =============================================================
   */
  var ProductZoomModal = class extends ModalContent {
    connectedCallback() {
      super.connectedCallback();
    }

    setActiveMedia(mediaId) {
      const activeMedia = this.querySelector(`[data-media-id="${ mediaId }"]`);

      this.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove("is-selected");
      });

      activeMedia.classList.add("is-selected");
    }
  }

  window.customElements.define("product-zoom-modal", ProductZoomModal);











  /**
   * =============================================================
   * ## PRODUCT META
   * - Controls product price changes
   * =============================================================
   */
  var ProductMeta = class extends HTMLElement {
    connectedCallback() {
      this.masterSelector = document.getElementById(this.getAttribute("form-id")).id;
      var _a;

      (_a = document.getElementById(this.getAttribute("form-id"))) == null ? void 0 : _a.addEventListener("variant:changed", this._onVariantChanged.bind(this));

      /**
       * Listen to personalisation change events meaning we need to either add
       * or remove the personalisation cost from a variant price
       */
      this.addEventListener("personalisation:changed", this._onPersonalisationChanged.bind(this));
      this.personalisation = this.querySelector(".pp");
      this.personalisationIsChecked;
    }

    _onVariantChanged(event, personalisationPrice) {
      if (personalisationPrice) {
        this.personalisationIsChecked = this.personalisation.querySelector("#personalisation-checkbox").checked;

        if (this.personalisationIsChecked) {
          personalisationPrice = this.personalisation.getAttribute("price");
        }
      }

      this._updatePrices(event.detail.variant, personalisationPrice);
    }

    _onPersonalisationChanged(event) {
      /**
       * By default, we want products with a single option to not select a variant.
       * These products show a complex price list such as "from price to price".
       * We don't want the personalisation price to be added to this, so we
       * return.
       */
      if (this.masterSelector.value == "") {
        return;
      }

      this._updatePrices(event.detail.variant, event.detail.personalisationPrice);
    }

    _updatePrices(variant, personalisationPrice) {
      let productPrices = this.querySelector("[data-product-price-list]");
      let currencyFormat = window.themeVariables.settings.moneyFormat;
      personalisationPrice = parseInt(personalisationPrice || 0);

      if (!productPrices) {
        return;
      }

      if (!variant) {
        productPrices.style.display = "none";
      } else {

        productPrices.innerHTML = "";

        // If we have a sale price
        if (variant["compare_at_price"] > variant["price"]) {
          productPrices.innerHTML += `<span class="price price--highlight"><span class="visually-hidden">${window.themeVariables.strings.productSalePrice}</span>${formatMoney(variant["price"] + personalisationPrice, currencyFormat)}</span>`;
          productPrices.innerHTML += `<span class="price price--compare"><span class="visually-hidden">${window.themeVariables.strings.productRegularPrice}</span>${formatMoney(variant["compare_at_price"] + personalisationPrice, currencyFormat)}</span>`;
        } else {
          productPrices.innerHTML += `<span class="price"><span class="visually-hidden">${window.themeVariables.strings.productSalePrice}</span>${formatMoney(variant["price"] + personalisationPrice, currencyFormat)}</span>`;
        }

        productPrices.style.display = "";
      }
    }
  };

  window.customElements.define("product-meta", ProductMeta);










  /**
   * =============================================================
   * ## PRODUCT VARIANTS
   * =============================================================
   */

  var ProductVariants = class extends CustomHTMLElement {

    async connectedCallback() {
      this.masterSelector = document.getElementById(this.getAttribute("form-id")).id;
      this.optionSelectors = Array.from(this.querySelectorAll("[data-selector-type]"));

      if (!this.masterSelector) {
        console.warn(`The variant selector for product with handle ${this.productHandle} is not linked to any product form.`);
        return;
      }

      this.product = await ProductLoader.load(this.productHandle);

      this.delegate.on("change", '[name^="option"]', this._onOptionChanged.bind(this));
      this.masterSelector.addEventListener("change", this._onMasterSelectorChanged.bind(this));

      this._updateDisableSelectors();

      // If we are NOT a single option product, we select a variant by default
      if (!this.singleOptionProduct) {
        this.selectVariant(this.selectedVariant["id"]);
      }
    }

    get singleOptionProduct() {
      return this.hasAttribute("single-option-product");
    }

    get selectedVariant() {
      return this._getVariantById(parseInt(this.masterSelector.value));
    }

    get productHandle() {
      return this.getAttribute("handle");
    }

    get updateUrl() {
      return this.hasAttribute("update-url");
    }

    selectVariant(id) {
      var _a;

      if (!this._isVariantSelectable(this._getVariantById(id))) {
        id = this._getFirstMatchingAvailableVariant()["id"];
      }

      if (((_a = this.selectedVariant) == null ? void 0 : _a.id) === id) {
        return;
      }

      this.masterSelector.value = id;

      this.masterSelector.dispatchEvent(new Event("change", { bubbles: true }));

      if (this.updateUrl && history.replaceState) {
        const newUrl = new URL(window.location.href);
        if (id) {
          newUrl.searchParams.set("variant", id);
        } else {
          newUrl.searchParams.delete("variant");
        }
        window.history.replaceState({ path: newUrl.toString() }, "", newUrl.toString());
      }

      this._updateDisableSelectors();

      triggerEvent(this.masterSelector.form, "variant:changed", { variant: this.selectedVariant });
    }

    _onOptionChanged() {
      var _a;
      this.selectVariant((_a = this._getVariantFromOptions()) == null ? void 0 : _a.id);
    }

    _onMasterSelectorChanged() {
      var _a;

      const options = ((_a = this.selectedVariant) == null ? void 0 : _a.options) || [];

      options.forEach((value, index) => {
        let input = this.querySelector(`input[name="option${index + 1}"][value="${CSS.escape(value)}"], select[name="option${index + 1}"]`); 
        let triggerChangeEvent = false;

        if (input.tagName === "SELECT") {
          triggerChangeEvent = input.value !== value;
          input.value = value;
        } else if (input.tagName === "INPUT") {
          triggerChangeEvent = !input.checked && input.value === value;
          input.checked = input.value === value;
        }

        if (triggerChangeEvent) {
          input.dispatchEvent(new Event("change", { bubbles: true }));
        }
      });
    }

    _getVariantById(id) {
      return this.product["variants"].find((variant) => variant["id"] === id);
    }

    _getVariantFromOptions() {
      const options = this._getSelectedOptionValues();

      return this.product["variants"].find((variant) => {
        return variant["options"].every((value, index) => value === options[index]);
      });
    }

    _isVariantSelectable(variant) {
      if (!variant) {
        return false;
      } else {
        return variant["available"] || !variant["available"];
      }
    }

    _getFirstMatchingAvailableVariant() {
      let options = this._getSelectedOptionValues();
      let matchedVariant = null; 
      let slicedCount = 0;

      do {
        options.pop();
        slicedCount += 1;
        matchedVariant = this.product["variants"].find((variant) => {
          return variant["available"] && variant["options"].slice(0, variant["options"].length - slicedCount).every((value, index) => value === options[index]);
        });
      } while (!matchedVariant && options.length > 0);

      return matchedVariant;
    }

    _getSelectedOptionValues() {
      const options = [];

      Array.from(this.querySelectorAll('input[name^="option"]:checked, select[name^="option"]')).forEach((option) => {
        options.push(option.value);
      });
      return options;
    }

    _updateDisableSelectors() {
      const selectedVariant = this.selectedVariant;

      if (!selectedVariant) {
        return;
      }

      const applyClassToSelector = (selector, valueIndex, available, hasAtLeastOneCombination) => {
        let selectorType = selector.getAttribute("data-selector-type");
        let cssSelector = `.combo-box__option-item:nth-child(${valueIndex + 1})`;

        selector.querySelector(cssSelector).toggleAttribute("hidden", !hasAtLeastOneCombination);

        selector.querySelector(cssSelector).classList.toggle("is-disabled", !available);
      };

      if (this.optionSelectors && this.optionSelectors[0]) {
        this.product["options"][0]["values"].forEach((value, valueIndex) => {

          const hasAtLeastOneCombination = this.product["variants"].some((variant) => variant["option1"] === value && variant);
          const hasAvailableVariant = this.product["variants"].some((variant) => variant["option1"] === value && variant["available"]);

          applyClassToSelector(this.optionSelectors[0], valueIndex, hasAvailableVariant, hasAtLeastOneCombination);

          if (this.optionSelectors[1]) {
            this.product["options"][1]["values"].forEach((value2, valueIndex2) => {
              const hasAtLeastOneCombination2 = this.product["variants"].some((variant) => variant["option2"] === value2 && variant["option1"] === selectedVariant["option1"] && variant);

              const hasAvailableVariant2 = this.product["variants"].some((variant) => variant["option2"] === value2 && variant["option1"] === selectedVariant["option1"] && variant["available"]);

              applyClassToSelector(this.optionSelectors[1], valueIndex2, hasAvailableVariant2, hasAtLeastOneCombination2);

              if (this.optionSelectors[2]) {
                this.product["options"][2]["values"].forEach((value3, valueIndex3) => {
                  const hasAtLeastOneCombination3 = this.product["variants"].some((variant) => variant["option3"] === value3 && variant["option1"] === selectedVariant["option1"] && variant["option2"] === selectedVariant["option2"] && variant);
                  
                  const hasAvailableVariant3 = this.product["variants"].some((variant) => variant["option3"] === value3 && variant["option1"] === selectedVariant["option1"] && variant["option2"] === selectedVariant["option2"] && variant["available"]);

                  applyClassToSelector(this.optionSelectors[2], valueIndex3, hasAvailableVariant3, hasAtLeastOneCombination3);
                });
              }
            });
          }
        });
      }
    }
  };

  window.customElements.define("product-variants", ProductVariants);










  /**
   * =============================================================
   * ## PRODUCT PAYMENT CONTAINER
   * =============================================================
   */
  var PaymentContainer = class extends HTMLElement {
    connectedCallback() {
      var _a;

      (_a = document.getElementById(this.getAttribute("form-id"))) == null ? void 0 : _a.addEventListener("variant:changed", this._onVariantChanged.bind(this));

      const scriptTag = this.querySelector("script");

      // this.preorderVariants = JSON.parse(scriptTag.innerHTML);
      // this.preorderMessage = this.querySelector(".preorder-message");
      // this.preorderMessageText = this.querySelector(".preorder-message__text");
    }

    get isPreorderProduct() {
      return this.hasAttribute("preorder-product");
    }

    get preorderProductMessage() {
      return this.getAttribute("preorder-product-message");
    }

    _onVariantChanged(event) {
      this._updateAddToCartButton(event.detail.variant);
      // this._updatePreorderVariantsMessage(event.detail.variant);
    }

    _updatePreorderVariantsMessage(variant) {
      if (this.preorderVariants[variant["id"]][0]["preorder"]) {
        this.preorderMessage.style.display = "block";
        this.preorderMessageText.innerText = this.preorderVariants[variant["id"]][0]["preorder_message"];
      } else if (this.isPreorderProduct) {
        this.preorderMessage.style.display = "block";
        this.preorderMessageText.innerText = this.preorderProductMessage;
      } else {
        this.preorderMessage.style.display = "none";
        this.preorderMessageText.innerText = "";
      }
    }

    _updateAddToCartButton(variant) {
      let addToCartButtonElement = this.querySelector("[data-product-add-to-cart-button]");

      if (!addToCartButtonElement) {
        return;
      }

      let addToCartButtonText = "";

      addToCartButtonElement.classList.remove("button--primary", "button--secondary");

      if (!variant) {
        addToCartButtonElement.setAttribute("disabled", "disabled");
        addToCartButtonElement.classList.add("button--secondary");
        addToCartButtonText = window.themeVariables.strings.productFormUnavailable;
      } else {
        if (variant["available"]) {
          addToCartButtonElement.removeAttribute("disabled");
          addToCartButtonElement.classList.add("button--primary");

          addToCartButtonText = addToCartButtonElement.getAttribute("data-button-content");
        } else {
          addToCartButtonElement.setAttribute("disabled", "disabled");
          addToCartButtonElement.classList.add("button--secondary");
          addToCartButtonText = window.themeVariables.strings.productFormSoldOut;
        }
      }

      if (addToCartButtonElement.getAttribute("is") === "loader-button") {
        addToCartButtonElement.firstElementChild.innerHTML = addToCartButtonText;
      } else {
        addToCartButtonElement.innerHTML = addToCartButtonText;
      }
    }
  };

  window.customElements.define("product-payment-container", PaymentContainer);










  /**
   * =============================================================
   * ## PRODUCT PERSONALISATION
   * =============================================================
   */
  var ProductPersonalisation = class extends HTMLElement {

    connectedCallback() {
      this.checkbox = this.querySelector("#personalisation-checkbox");
      this.details = this.querySelector(".pp__details");
      this.label = this.querySelector(".pp__form-label");
      this.charactersRemaining = this.querySelector(".pp__form-characters-remaining");
      this.input = this.querySelector("#personalisation-input");

      // This allows us to get the selected variant so we can update price when personalisation is checked
      this.personalisationPrice = this.getAttribute("price");
      this.ProductVariants = document.querySelector('.product-form__variants[form-id="' + this.getAttribute("form-id") + '"]');
      this.masterSelector = document.getElementById(this.getAttribute("form-id")).id;

      // Set characters remaining value based on input length
      this.charactersRemaining.innerText = this.input.value.length;

      // Event listeners
      this.checkbox.addEventListener("change", this._onCheckboxChanged.bind(this));
      this.input.addEventListener("keyup", this._onKeyUp.bind(this));
    }

    get singleVariant() {
      return this.hasAttribute("single-variant");
    }

    _onCheckboxChanged(event) {
      // Get currently selected variant
       let variant = this.ProductVariants._getVariantById(parseInt(this.masterSelector.value));

      /**
       * We trigger event changes based on the personalisation checkbox.
       * This is listened to in the `ProductMeta` component which will
       * update the visible variant price.
       */
      if (event.target.checked) {
        this.details.setAttribute("open", "");
        triggerEvent(this, "personalisation:changed", { variant: variant, personalisationPrice: this.personalisationPrice });
      } else {
        this.details.removeAttribute("open");
        triggerEvent(this, "personalisation:changed", { variant: variant, personalisationPrice: 0 });
      }
    }

    _onKeyUp(event) {
      // Update characters remaining element
      this.charactersRemaining.innerText = this.input.value.length;
    }
  };

  window.customElements.define("product-personalisation", ProductPersonalisation);











  /**
   * =============================================================
   * ## PRODUCT CROSS SELL
   * =============================================================
   */
  var ProductCrossSell = class extends HTMLElement {

    connectedCallback() {
      this.checkbox = this.querySelector("#cross-sell-checkbox");
    }

    get crossSellVariantId() {
      return this.getAttribute("data-cross-sell-variant-id");
    }
  };

  window.customElements.define("product-cross-sell", ProductCrossSell);










  /**
   * =============================================================
   * ## LINE ITEM PERSONALISATION
   * =============================================================
   */
  var LineItemPersonalisation = class extends HTMLElement {

    connectedCallback() {
      this.editButton = this.querySelector(".line-item__edit");
      this.editContainer = this.querySelector(".line-item-edit");
      this.checkbox = this.querySelector(".pp__checkbox");
      this.details = this.querySelector(".pp__details");
      this.label = this.querySelector(".pp__form-label");
      this.charactersRemaining = this.querySelector(".pp__form-characters-remaining");
      this.input = this.querySelector(".pp__form-input");
      this.updateButton = this.querySelector(".pp__form-update");
      this.cancelButton = this.querySelector(".pp__form-cancel");
      this.parentLineItem = this.closest(".line-item");
      this.cartForm = this.closest("form");
      this.ppVariantId = this.parentLineItem.getAttribute("data-pp-variant-id");

      // Set characters remaining value based on input length
      this.charactersRemaining.innerText = this.input.value.length;

      // Event listeners
      this.editButton.addEventListener("click", this._onEditButtonClick.bind(this));
      this.checkbox.addEventListener("change", this._onCheckboxChanged.bind(this));
      this.input.addEventListener("keyup", this._onKeyUp.bind(this));
      this.updateButton.addEventListener("click", this._update.bind(this));
      this.cancelButton.addEventListener("click", this._cancel.bind(this));
    }

    _startLoading() {
      document.querySelector(".loader").classList.add("is-loading");
    }

    _endLoading() {
      document.querySelector(".loader").classList.remove("is-loading");
    }

    _onEditButtonClick(event) {
      event.preventDefault();

      if (this.editIsOpen) {
        this.editContainer.removeAttribute("open");
      } else {
        this.editContainer.setAttribute("open", "");
      }
    }

    get editIsOpen() {
      return this.editContainer.hasAttribute("open");
    }

    get hasPersonalisation() {
      return this.parentLineItem.hasAttribute("data-parent-ppid");
    }

    _onCheckboxChanged(event) {
      if (event.target.checked) {
        this.details.setAttribute("open", "");
      } else {
        this.details.removeAttribute("open");
      }
    }

    _onKeyUp(event) {
      // Update characters remaining element
      this.charactersRemaining.innerText = this.input.value.length;
    }

    _cancel(event) {
      event.preventDefault();

      // If product has personalisation, reset all values
      if (this.parentLineItem.hasAttribute("data-parent-ppid")) {
        this.checkbox.checked = true;
        this.input.value = this.parentLineItem.getAttribute("data-parent-pp-name");
        this.details.setAttribute("open", "");
        this.editContainer.removeAttribute("open");
      } else {
        // Else clear all inputs and uncheck checkbox
        this.checkbox.checked = false;
        this.input.value = "";
        this.details.removeAttribute("open");
        this.editContainer.removeAttribute("open");
      }
    }

    async _update(event) {
      event.preventDefault();

      var ppId;

      if (this.hasPersonalisation) {
        ppId = this.parentLineItem.getAttribute("data-parent-ppid");

        if (!this.checkbox.checked) {
          /**
           * The user has unchecked the personalisation input so wants to remove it.
           * We need to:
           * 1. Remove the associated personalisation line item
           * 2. Update the existing line item to remove the line item property for
           *   personalisation
           */

          if (window.themeVariables.settings.pageType === "cart") {
            // window.location.href = link;
            // return;
            this._startLoading();
          }

          // Get personalisation product
          var ppProduct = this.cartForm.querySelector('[data-pp-product="' + ppId + '"]');

          // Get line item ID of personalisation product
          var ppProductLineId = ppProduct.getAttribute('data-parent-line-id');

          // Get line item ID of main product
          var parentLineId = this.parentLineItem.getAttribute('data-parent-line-id');

          const response = await fetch(`${window.themeVariables.routes.cartChangeUrl}.js`, {
            body: JSON.stringify({
              "id": ppProductLineId,
              "quantity": 0
            }),
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            }
          });

          if (!response.ok) {
            this._endLoading();
            return;
          }

          const response2 = await fetch(`${window.themeVariables.routes.cartChangeUrl}.js`, {
            body: JSON.stringify({
              "id": parentLineId,
              "properties": {
                "__ppExists": false
              },
              "sections": ["mini-cart"]
            }),
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            }
          });

          const cartContent = await response2.json();

          if (window.themeVariables.settings.pageType === "cart") {
            window.location.reload();
          }

          document.documentElement.dispatchEvent(new CustomEvent("cart:updated", {
            bubbles: true,
            detail: {
              cart: cartContent
            }
          }));

          document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
            bubbles: true,
            detail: {
              cart: cartContent
            }
          }));
        }

        if (this.checkbox.checked) {
          let existingName = this.parentLineItem.getAttribute("data-parent-pp-name");
          let newName = this.input.value.trim();

          // User has not changed name so we close the edit and return
          if (existingName === newName) {
            this.editContainer.removeAttribute("open");
            return;
          }

          // The user has not specified a name so we run an alert
          if (newName === '') {
            alert(window.themeVariables.strings.ppError);
            return;
          }

          if (window.themeVariables.settings.pageType === "cart") {
            // window.location.href = link;
            // return;
            this._startLoading();
          }
          /**
           * The user has updated the personalisation name. We need to:
           * 1. Get the new name
           * 2. Update the line item property of the existing item.
           * 3. Update the line item property of the personalisation product.
           */

          // Get line item ID of main product
          var parentLineId = this.parentLineItem.getAttribute("data-parent-line-id");

          // Get variant ID of main product
          var variantId = this.parentLineItem.getAttribute("data-variant-id");

          // Get line item ID of personalisation product
          var ppProductLineId = this.cartForm.querySelector('[data-pp-product="' + ppId + '"]').getAttribute("data-parent-line-id");

          var properties = {
            "Personalisation": newName,
            "_ppId": variantId + '_' + newName
          };

          // Update the personalisation product
          const response = await fetch(`${window.themeVariables.routes.cartChangeUrl}.js`, {
            body: JSON.stringify({
              "id": ppProductLineId,
              "properties": properties
            }),
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            }
          });

          if (!response.ok) {
            this._endLoading();
            return;
          }

          // Update the main product
          const response2 = await fetch(`${window.themeVariables.routes.cartChangeUrl}.js`, {
            body: JSON.stringify({
              "id": parentLineId,
              "properties": properties,
              "sections": ["mini-cart"]
            }),
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            }
          });

          const cartContent = await response2.json();

          if (window.themeVariables.settings.pageType === "cart") {
            window.location.reload();
          }

          document.documentElement.dispatchEvent(new CustomEvent("cart:updated", {
            bubbles: true,
            detail: {
              cart: cartContent
            }
          }));

          document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
            bubbles: true,
            detail: {
              cart: cartContent
            }
          }));


        }
      } else {
        // The user is adding personalisation

        /**
         * The user clicks update without selecting personalisation.
         * We close the edit container and return
         */
        if (!this.checkbox.checked) {
          return;
        }

        /**
         * The user wants to add personalisation
         */
        if (this.checkbox.checked) {
          let newName = this.input.value.trim();

          // The user has not specified a name so we run an alert
          if (newName === '') {
            alert(window.themeVariables.strings.ppError);
            return;
          }

          if (window.themeVariables.settings.pageType === "cart") {
            // window.location.href = link;
            // return;
            this._startLoading();
          }

          // Get line item ID of main product
          var parentLineId = this.parentLineItem.getAttribute('data-parent-line-id');

          // Get variant ID of main product
          var variantId = this.parentLineItem.getAttribute("data-variant-id");

          // Get qty of main product
          var quantity = this.parentLineItem.getAttribute("data-parent-qty");

          var properties = {
            "Personalisation": newName,
            "_ppId": variantId + '_' + newName
          };

          // Add the personalisation product
          const response = await fetch(`${window.themeVariables.routes.cartAddUrl}.js`, {
            body: JSON.stringify({
              "id": this.ppVariantId,
              "quantity": quantity,
              "properties": properties
            }),
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Requested-With": "XMLHttpRequest"
            }
          });

          if (!response.ok) {
            this._endLoading();
            return;
          }

          // Update the main product
          const response2 = await fetch(`${window.themeVariables.routes.cartChangeUrl}.js`, {
            body: JSON.stringify({
              "id": parentLineId,
              "properties": properties,
              "sections": ["mini-cart"]
            }),
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            }
          });

          const cartContent = await response2.json();

          if (window.themeVariables.settings.pageType === "cart") {
            window.location.reload();
          }

          document.documentElement.dispatchEvent(new CustomEvent("cart:updated", {
            bubbles: true,
            detail: {
              cart: cartContent
            }
          }));

          document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
            bubbles: true,
            detail: {
              cart: cartContent
            }
          }));
        }
      }
    }
  };

  window.customElements.define("line-item-personalisation", LineItemPersonalisation);









  /**
   * =============================================================
   * ## PRODUCT FORM
   * =============================================================
   */
  var ProductForm = class extends HTMLFormElement {
    connectedCallback() {
      this.id.disabled = false;
      this.masterSelector = this.id;
      this.quantityInput = document.querySelector('.quantity-selector__input[form="' + this.getAttribute("id") + '"]');
      this.crossSell = document.querySelector("product-cross-sell");

      if (this.crossSell) {
        this.crossSellCheckbox = this.crossSell.querySelector("#cross-sell-checkbox");
      }

      if (window.themeVariables.settings.cartType === "page" || window.themeVariables.settings.pageType === "cart") {
        return;
      }

      // If we have personalisation, we store multiple variables
      if (this.hasPersonalisation) {
        this.personalisation = document.querySelector('.pp[form-id="' + this.getAttribute("id") + '"]');

        this.personalisationCheckbox = this.personalisation.querySelector("#personalisation-checkbox");

        this.personalisationInput =  this.personalisation.querySelector("#personalisation-input");

        this.personalisationVariantId = this.personalisation.getAttribute("data-pp-variant-id");

        this.hiddenInput = this.personalisation.querySelector("#HiddenInput");
      }

      this.addEventListener("submit", this._onSubmit.bind(this));
    }

    get hasPersonalisation() {
      return this.hasAttribute("has-personalisation");
    }

    async _onSubmit(event) {
      event.preventDefault();

      if (this.hasPersonalisation && this.personalisationCheckbox.checked && this.personalisationInput.value.trim() == '') {
        alert(window.themeVariables.strings.ppError);
        return;
      }

      if (!this.checkValidity()) {
        this.reportValidity();
        return;
      }

      const submitButtons = Array.from(this.elements).filter((button) => button.type === "submit");
      submitButtons.forEach((submitButton) => {
        submitButton.setAttribute("disabled", "disabled");
        submitButton.setAttribute("aria-busy", "true");
      });

      /**
       * Empty variables that will help construct our eventual fetch request based
       * on whether we have personalisation or not 
       */
      let data;
      let properties = {};
      let fetchHeaders = {};

      var productForm;

      if (this.hasPersonalisation && this.personalisationCheckbox.checked) {
        /**
         * If user selects personalisation, we need to add two products to cart:
         * the main product and the personalisation product.
         * 
         * We attach line item properties, and construct a data object.
         * We also need to submit the content as JSON.
         */
        properties = {
          'Personalisation': this.personalisationInput.value,
          '_ppId': this.masterSelector.value + '_' + this.personalisationInput.value
        }

        data = {
          items: [
            {
              id: this.masterSelector.value,
              quantity: this.quantityInput.value,
              properties: properties
            },
            {
              id: this.personalisationVariantId,
              quantity: this.quantityInput.value,
              properties: properties
            }
          ],
          sections: "mini-cart"
        }

        // Cross sell product
        if (this.crossSell && this.crossSellCheckbox.checked) {
          data["items"].push({
            id: this.crossSell.getAttribute("data-cross-sell-variant-id"),
            quantity: this.quantityInput.value
          });
        }

        productForm = JSON.stringify(data);
        fetchHeaders = {
          "X-Requested-With": "XMLHttpRequest",
          "Content-Type": "application/json"
        };
      } else {

        // Cross sell product
        if (this.crossSell && this.crossSellCheckbox.checked) {
          data = {
            items: [
              {
                id: this.masterSelector.value,
                quantity: this.quantityInput.value,
                properties: properties
              },
              {
                id: this.crossSell.getAttribute("data-cross-sell-variant-id"),
                quantity: this.quantityInput.value
              }
            ],
            sections: "mini-cart"
          }

          productForm = JSON.stringify(data);
          fetchHeaders = {
            "X-Requested-With": "XMLHttpRequest",
            "Content-Type": "application/json"
          };
        } else {
          /**
           * If we are adding to cart a single product, we can simply use the
           * existing data from the product form
           */
          productForm = new FormData(this);
          productForm.append("sections", ["mini-cart"]);;

          productForm.delete("option1");
          productForm.delete("option2");
          productForm.delete("option3");

          fetchHeaders = {
            "X-Requested-With": "XMLHttpRequest",
          };
        }
      }

      const response = await fetch(`${window.themeVariables.routes.cartAddUrl}.js`, {
        body: productForm,
        method: "POST",
        headers: fetchHeaders
      });

      submitButtons.forEach((submitButton) => {
        submitButton.removeAttribute("disabled");
        submitButton.removeAttribute("aria-busy");
      });

      const responseJson = await response.json();

      if (response.ok) {
        this.dispatchEvent(new CustomEvent("variant:added", {
          bubbles: true,
          detail: {
            variant: responseJson.hasOwnProperty("items") ? responseJson["items"][0] : responseJson
          }
        }));

        fetch(`${window.themeVariables.routes.cartUrl}.js`).then(async (response2) => {

          const cartContent = await response2.json();

          document.documentElement.dispatchEvent(new CustomEvent("cart:updated", {
            bubbles: true,
            detail: {
              cart: cartContent
            }
          }));
        });

        document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
          bubbles: true,
          detail: {
            cart: responseJson,
            openMiniCart: window.themeVariables.settings.cartType === "drawer"
          }
        }));
      }

      this.dispatchEvent(new CustomEvent("cart-notification:show", {
        bubbles: true,
        cancelable: true,
        detail: {
          status: response.ok ? "success" : "error",
          error: responseJson["description"] || ""
        }
      }));
    }
  };

  window.customElements.define("product-form", ProductForm, { extends: "form" });









  /**
   * =============================================================
   * ## CART COUNT
   * =============================================================
   */
  var CartCount = class extends CustomHTMLElement {
    connectedCallback() {
      this.rootDelegate.on("cart:updated", (event) => this.innerText = event.detail.cart["item_count"]);
    }
  };

  window.customElements.define("cart-count", CartCount);










  /**
   * =============================================================
   * ## LINE ITEM
   * =============================================================
   */

  var LineItem = class extends HTMLElement {
    connectedCallback() {
      this.lineItemLoader = this.querySelector(".line-item__loader");
      this.addEventListener("line-item-quantity:change:start", this._onQuantityStart.bind(this));
      this.addEventListener("line-item-quantity:change:end", this._onQuantityEnd.bind(this));
    }

    _onQuantityStart() {
      if (!this.lineItemLoader || !supportsAnimationsAPI) {
        return;
      }

      this.lineItemLoader.hidden = false;
      this.lineItemLoader.firstElementChild.hidden = false;
      this.lineItemLoader.lastElementChild.hidden = true;
    }

    async _onQuantityEnd(event) {
      if (event.detail.cart["item_count"] === 0) {
        return;
      }

      if (this.lineItemLoader && supportsAnimationsAPI) {
        await this.lineItemLoader.firstElementChild.animate({ opacity: [1, 0], transform: ["translateY(0)", "translateY(-10px)"] }, 75).finished;

        this.lineItemLoader.firstElementChild.hidden = true;

        if (event.detail.newLineQuantity === 0) {
          await this.animate({ opacity: [1, 0], height: [`${this.clientHeight}px`, 0] }, { duration: 300, easing: "ease" }).finished;

          this.remove();
        } else {
          this.lineItemLoader.lastElementChild.hidden = false;

          await this.lineItemLoader.lastElementChild.animate({ opacity: [0, 1], transform: ["translateY(10px)", "translateY(0)"] }, { duration: 75, endDelay: 300 }).finished;

          this.lineItemLoader.hidden = true;
        }
      }
    }
  };

  window.customElements.define("line-item", LineItem);










  /**
   * =============================================================
   * ## LINE ITEM QUANTITY
   * =============================================================
   */
  var LineItemQuantity = class extends CustomHTMLElement {
    connectedCallback() {
      this.delegate.on("click", "a", this._onQuantityLinkClicked.bind(this));
      this.delegate.on("change", "input", this._onQuantityChanged.bind(this));
      this.parentLineItem = this.closest(".line-item");
      this.cartForm =  this.closest("form");
    }

    _startLoading() {
      document.querySelector(".loader").classList.add("is-loading");
    }

    _endLoading() {
      document.querySelector(".loader").classList.remove("is-loading");
    }

    _onQuantityLinkClicked(event, target) {
      event.preventDefault();

      if (target.hasAttribute("data-ppid")) {
        this._updatePersonalisation(target.getAttribute("data-qty-change"));
      } else {
        this._updateFromLink(target.href);
      }
    }

    _onQuantityChanged(event, target) {
      if (target.hasAttribute("data-ppid")) {
        this._updatePersonalisation(target.value);
      } else {
        this._updateFromLink(`${window.themeVariables.routes.cartChangeUrl}?quantity=${target.value}&line=${target.getAttribute("data-line")}`);
      }
    }

    async _updatePersonalisation(quantity) {
      if (window.themeVariables.settings.pageType === "cart") {
        // window.location.href = link;
        // return;
        this._startLoading();
      }

      let data = {};
      let updates = {};
      let fetchBody;

      let ppId = this.parentLineItem.getAttribute("data-parent-ppid");

      this.cartForm.querySelectorAll('[data-parent-ppid]').forEach((item) => {
        let parentPPID = item.getAttribute("data-parent-ppid");

        if (parentPPID == ppId) {
          var type = item.getAttribute("data-parent-line-id");
          var value = parseInt(quantity);
          var id = "8692039909428:1d9b25d283b132cf7a01a452bf2380f8"

          updates[type] = value;
        }
      });

      data = {
        "updates": updates,
        "sections": ["mini-cart"]
      };
      
      fetchBody = JSON.stringify(data);

      this.dispatchEvent(new CustomEvent("line-item-quantity:change:start", { bubbles: true, detail: { newLineQuantity: quantity } }));

      const response = await fetch(`${window.themeVariables.routes.cartUpdateUrl}.js`, {
        body: fetchBody,
        credentials: "same-origin",
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        }
      });

      if (!response.ok) {
        this._endLoading();
        return;
      }

      const cartContent = await response.json();

      this.dispatchEvent(new CustomEvent("line-item-quantity:change:end", { bubbles: true, detail: { cart: cartContent, newLineQuantity: quantity } }));

      if (window.themeVariables.settings.pageType === "cart") {
        window.location.reload();
      }

      document.documentElement.dispatchEvent(new CustomEvent("cart:updated", {
        bubbles: true,
        detail: {
          cart: cartContent
        }
      }));

      document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
        bubbles: true,
        detail: {
          cart: cartContent,
          replacementDelay: quantity === 0 ? 600 : 750
        }
      }));
    }

    async _updateFromLink(link) {
      if (window.themeVariables.settings.pageType === "cart") {
        this._startLoading();
      }

      const changeUrl = new URL(link, `https://${window.themeVariables.routes.host}`); 
      const searchParams = changeUrl.searchParams;
      const line = searchParams.get("line"); 
      const id = searchParams.get("id"); 
      const quantity = parseInt(searchParams.get("quantity"));

      this.dispatchEvent(new CustomEvent("line-item-quantity:change:start", { bubbles: true, detail: { newLineQuantity: quantity } }));

      const response = await fetch(`${window.themeVariables.routes.cartChangeUrl}.js`, {
        body: JSON.stringify({ line, id, quantity, sections: ["mini-cart"] }),
        credentials: "same-origin",
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        }
      });

      if (!response.ok) {
        this._endLoading();
        return;
      }

      const cartContent = await response.json();

      this.dispatchEvent(new CustomEvent("line-item-quantity:change:end", { bubbles: true, detail: { cart: cartContent, newLineQuantity: quantity } }));

      if (window.themeVariables.settings.pageType === "cart") {
        window.location.reload();
      }

      document.documentElement.dispatchEvent(new CustomEvent("cart:updated", {
        bubbles: true,
        detail: {
          cart: cartContent
        }
      }));

      document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
        bubbles: true,
        detail: {
          cart: cartContent,
          replacementDelay: quantity === 0 ? 600 : 750
        }
      }));
    }
  };
  
  window.customElements.define("line-item-quantity", LineItemQuantity);









  /**
   * =============================================================
   * ## FREE SHIPPING BAR
   * =============================================================
   */

  var FreeShippingBar = class extends HTMLElement {
    connectedCallback() {
      document.documentElement.addEventListener("cart:updated", this._onCartUpdated.bind(this));
    }

    get threshold() {
      return parseFloat(this.getAttribute("threshold"));
    }

    _onCartUpdated(event) {
      this.style.setProperty("--progress", Math.min(parseFloat(event.detail["cart"]["total_price"]) / this.threshold, 1));
    }
  };
  
  window.customElements.define("free-shipping-bar", FreeShippingBar);









  /**
   * =============================================================
   * ## CART NOTIFICATION
   * =============================================================
   */
  var CartNotification = class extends CustomHTMLElement {
    connectedCallback() {
      this.rootDelegate.on("cart-notification:show", this._onShow.bind(this), !this.hasAttribute("global"));
      this.delegate.on("click", '[data-action="close"]', (event) => {
        event.stopPropagation();
        this.hidden = true;
      });

      this.addEventListener("mouseenter", this.stopTimer.bind(this));
      this.addEventListener("mouseleave", this.startTimer.bind(this));
      window.addEventListener("pagehide", () => this.hidden = true);
    }

    set hidden(value) {
      if (!value) {
        this.startTimer();
      } else {
        this.stopTimer();
      }
      this.toggleAttribute("hidden", value);
    }

    get isInsideDrawer() {
      return this.classList.contains("cart-notification--drawer");
    }

    stopTimer() {
      clearTimeout(this._timeout);
    }

    startTimer() {
      this._timeout = setTimeout(() => this.hidden = true, 3e3);
    }

    _onShow(event) {

      if (this.isInsideDrawer && !this.closest(".drawer").open) {
        return;
      }

      if (this.hasAttribute("global") && event.detail.status === "success" && window.themeVariables.settings.cartType === "drawer") {
        return;
      }

      event.stopPropagation();

      let closeButtonHtml = "";
      if (!this.isInsideDrawer) {
        closeButtonHtml = `
        <button class="cart-notification__close o-tap-area small--hide" data-action="close">
          <span class="visually-hidden">${window.themeVariables.strings.accessibilityClose}</span>
          <svg focusable="false" width="14" height="14" class="icon icon--close icon--inline" viewBox="0 0 14 14">
            <path d="M13 13L1 1M13 1L1 13" stroke="currentColor" stroke-width="2" fill="none"></path>
          </svg>
        </button>
      `;
      }
      if (event.detail.status === "success") {
        this.classList.remove("cart-notification--error");
        this.innerHTML = `
        <div class="cart-notification__overflow">
          <div class="o-container">
            <div class="cart-notification__wrapper">
              <svg focusable="false" width="20" height="20" class="icon icon--cart-notification" viewBox="0 0 20 20">
                <rect width="20" height="20" rx="10" fill="currentColor"></rect>
                <path d="M6 10L9 13L14 7" fill="none" stroke="rgb(var(--color-success))" stroke-width="2"></path>
              </svg>
              
              <div class="cart-notification__text-wrapper">
                <span class="cart-notification__heading heading small--hide">${window.themeVariables.strings.cartItemAdded}</span>
                <span class="cart-notification__heading heading medium-up--hide">${window.themeVariables.strings.cartItemAddedShort}</span>
                <a href="${window.themeVariables.routes.cartUrl}" class="cart-notification__view-cart link">${window.themeVariables.strings.cartViewCart}</a>
              </div>
              
              ${closeButtonHtml}
            </div>
          </div>
        </div>
      `;
      } else {
        this.classList.add("cart-notification--error");
        this.innerHTML = `
        <div class="cart-notification__overflow">
          <div class="o-container">
            <div class="cart-notification__wrapper">
              <svg focusable="false" width="20" height="20" class="icon icon--cart-notification" viewBox="0 0 20 20">
                <rect width="20" height="20" rx="10" fill="currentColor"></rect>
                <path d="M9.6748 13.2798C9.90332 13.0555 10.1763 12.9434 10.4937 12.9434C10.811 12.9434 11.0819 13.0555 11.3062 13.2798C11.5347 13.5041 11.6489 13.7749 11.6489 14.0923C11.6489 14.4097 11.5347 14.6847 11.3062 14.9175C11.0819 15.146 10.811 15.2603 10.4937 15.2603C10.1763 15.2603 9.90332 15.146 9.6748 14.9175C9.45052 14.6847 9.33838 14.4097 9.33838 14.0923C9.33838 13.7749 9.45052 13.5041 9.6748 13.2798ZM9.56689 12.1816V5.19922H11.4141V12.1816H9.56689Z" fill="rgb(var(--color-error))"></path>
              </svg>
              
              <div class="cart-notification__text-wrapper">
                <span class="cart-notification__heading heading">${event.detail.error}</span>
              </div>
              
              ${closeButtonHtml}
            </div>
          </div>
        </div>
      `;
      }
      this.clientHeight;
      this.hidden = false;
    }
  };

  window.customElements.define("cart-notification", CartNotification);










  /**
   * =============================================================
   * ## CART DRAWER
   * =============================================================
   */

  var CartDrawer = class extends DrawerContent {
    connectedCallback() {
      super.connectedCallback();
      this.nextReplacementDelay = 0;
      this.rootDelegate.on("cart:refresh", this._rerenderCart.bind(this));
      this.addEventListener("variant:added", () => this.nextReplacementDelay = 600);
    }

    async _rerenderCart(event) {
      var _a;
      let cartContent = null, html = "";

      if (event.detail && event.detail["cart"]) {
        cartContent = event.detail["cart"];
        html = event.detail["cart"]["sections"]["mini-cart"];
      } else {
        const response = await fetch(`${window.themeVariables.routes.cartUrl}?section_id=${this.getAttribute("section")}`);
        html = await response.text();
      }

      const fakeDiv = document.createElement("div");
      fakeDiv.innerHTML = html;

      setTimeout(async () => {
        var _a2;
        const previousPosition = this.querySelector(".drawer__content").scrollTop;

        this.innerHTML = fakeDiv.querySelector("cart-drawer").innerHTML;

        if (cartContent && cartContent["item_count"] === 0 && supportsAnimationsAPI) {
          this.querySelector(".drawer__content").animate({ opacity: [0, 1], transform: ["translateY(40px)", "translateY(0)"] }, { duration: 450, easing: "cubic-bezier(0.33, 1, 0.68, 1)" });
        } else {
          this.querySelector(".drawer__content").scrollTop = previousPosition;
        }

        if ((_a2 = event == null ? void 0 : event.detail) == null ? void 0 : _a2.openMiniCart) {
          this.clientWidth;
          this.open = true;
        }
      }, ((_a = event == null ? void 0 : event.detail) == null ? void 0 : _a.replacementDelay) || this.nextReplacementDelay);
      this.nextReplacementDelay = 0;
    }

    async attributeChangedCallback(name, oldValue, newValue) {
      super.attributeChangedCallback(name, oldValue, newValue);

      switch (name) {
        case "open":
          if (this.open) {
            this.querySelector(".drawer__content").scrollTop = 0;
          }
      }
    }
  };

  window.customElements.define("cart-drawer", CartDrawer);










  /**
   * =============================================================
   * ## SHIPPING ESTIMATOR
   * =============================================================
   */

  var ShippingEstimator = class extends HTMLElement {
    connectedCallback() {
      this.submitButton = this.querySelector('[type="button"]');
      this.submitButton.addEventListener("click", this._estimateShipping.bind(this));
    }
    async _estimateShipping() {
      const zip = this.querySelector('[name="shipping-estimator[zip]"]').value, country = this.querySelector('[name="shipping-estimator[country]"]').value, province = this.querySelector('[name="shipping-estimator[province]"]').value;
      this.submitButton.setAttribute("aria-busy", "true");
      const prepareResponse = await fetch(`${window.themeVariables.routes.cartUrl}/prepare_shipping_rates.json?shipping_address[zip]=${zip}&shipping_address[country]=${country}&shipping_address[province]=${province}`, { method: "POST" });
      if (prepareResponse.ok) {
        const shippingRates = await this._getAsyncShippingRates(zip, country, province);
        this._formatShippingRates(shippingRates);
      } else {
        const jsonError = await prepareResponse.json();
        this._formatError(jsonError);
      }
      this.submitButton.removeAttribute("aria-busy");
    }
    async _getAsyncShippingRates(zip, country, province) {
      const response = await fetch(`${window.themeVariables.routes.cartUrl}/async_shipping_rates.json?shipping_address[zip]=${zip}&shipping_address[country]=${country}&shipping_address[province]=${province}`);
      const responseAsText = await response.text();
      if (responseAsText === "null") {
        return this._getAsyncShippingRates(zip, country, province);
      } else {
        return JSON.parse(responseAsText)["shipping_rates"];
      }
    }
    _formatShippingRates(shippingRates) {
      var _a;
      (_a = this.querySelector(".shipping-estimator__results")) == null ? void 0 : _a.remove();
      let formattedShippingRates = "";
      shippingRates.forEach((shippingRate) => {
        formattedShippingRates += `<li>${shippingRate["presentment_name"]}: ${formatMoney(parseFloat(shippingRate["price"]) * 100)}</li>`;
      });
      const html = `
      <div class="shipping-estimator__results">
        <p>${shippingRates.length === 0 ? window.themeVariables.strings.shippingEstimatorNoResults : shippingRates.length === 1 ? window.themeVariables.strings.shippingEstimatorOneResult : window.themeVariables.strings.shippingEstimatorMultipleResults}</p>
        ${formattedShippingRates === "" ? "" : `<ul class="unordered-list">${formattedShippingRates}</ul>`}
      </div>
    `;
      this.insertAdjacentHTML("beforeend", html);
    }
    _formatError(errors) {
      var _a;
      (_a = this.querySelector(".shipping-estimator__results")) == null ? void 0 : _a.remove();
      let formattedShippingRates = "";
      Object.keys(errors).forEach((errorKey) => {
        formattedShippingRates += `<li>${errorKey} ${errors[errorKey]}</li>`;
      });
      const html = `
      <div class="shipping-estimator__results">
        <p>${window.themeVariables.strings.shippingEstimatorError}</p>
        <ul class="unordered-list">${formattedShippingRates}</ul>
      </div>
    `;
      this.insertAdjacentHTML("beforeend", html);
    }
  };
  window.customElements.define("shipping-estimator", ShippingEstimator);










  /**
   * =============================================================
   * ## SEARCH PAGE
   * =============================================================
   */

  var SearchPage = class extends HTMLElement {
    connectedCallback() {
      this.facetToolbar = document.getElementById("mobile-facet-toolbar");
      this.tabsNav = document.getElementById("search-tabs-nav");

      this.tabsNav.addEventListener("tabs-nav:changed", this._onCategoryChanged.bind(this));

      this._completeSearch();
    }

    get terms() {
      return this.getAttribute("terms");
    }

    get completeFor() {
      return JSON.parse(this.getAttribute("complete-for")).filter((item) => !(item === ""));
    }

    async _completeSearch() {
      const promisesList = [];

      this.completeFor.forEach((item) => {
        promisesList.push(fetch(`${window.themeVariables.routes.searchUrl}?section_id=${this.getAttribute("section-id")}&q=${this.terms}&type=${item}&options[prefix]=last&options[unavailable_products]=${window.themeVariables.settings.searchUnavailableProducts}`));
      });

      const responses = await Promise.all(promisesList);

      await Promise.all(responses.map(async (response) => {
        const div = document.createElement("div");
        div.innerHTML = await response.text();

        const categoryResultDiv = div.querySelector(".main-search__category-result");
        const tabNavItem = div.querySelector("#search-tabs-nav .tabs-nav__item");

        if (categoryResultDiv) {
          categoryResultDiv.setAttribute("hidden", "");
          this.insertAdjacentElement("beforeend", categoryResultDiv);
          this.tabsNav.addButton(tabNavItem);
        }
      }));
    }

    _onCategoryChanged(event) {
      const button = event.detail.button;

      this.facetToolbar.classList.toggle("is-collapsed", button.getAttribute("data-type") !== "product");
    }
  };

  window.customElements.define("search-page", SearchPage);











  /**
   * =============================================================
   * ## EVENT MODAL
   * =============================================================
   */
   
  var EventModal = class extends ModalContent {
    connectedCallback() {
      super.connectedCallback();
    }
  }

  window.customElements.define("event-modal", EventModal);









  /**
   * =============================================================
   * ## PRODUCT LIST
   * =============================================================
   */

  var ProductList = class extends CustomHTMLElement {
    constructor() {
      super();
      this.productListInner = this.querySelector(".product-list__inner");
      this.productItems = Array.from(this.querySelectorAll("product-item"));
    }

    connectedCallback() {
      this.addEventListener("prev-next:prev", this.previous.bind(this));
      this.addEventListener("prev-next:next", this.next.bind(this));
    }


    previous(event) {
      const columnGap = parseInt(getComputedStyle(this).getPropertyValue("--product-list-column-gap"));

      event.target.nextElementSibling.removeAttribute("disabled");

      event.target.toggleAttribute("disabled", this.productListInner.scrollLeft - (this.productListInner.clientWidth + columnGap) <= 0);

      this.productListInner.scrollBy({ left: -(this.productListInner.clientWidth + columnGap), behavior: "smooth" });
    }

    next(event) {
      const columnGap = parseInt(getComputedStyle(this).getPropertyValue("--product-list-column-gap"));

      event.target.previousElementSibling.removeAttribute("disabled");

      event.target.toggleAttribute("disabled", this.productListInner.scrollLeft + (this.productListInner.clientWidth + columnGap) * 2 >= this.productListInner.scrollWidth);

      this.productListInner.scrollBy({ left: (this.productListInner.clientWidth + columnGap), behavior: "smooth" });
    }

  };
  // __publicField(ProductList, "observedAttributes", ["hidden"]);
  
  window.customElements.define("product-list", ProductList);










  /**
   * =============================================================
   * ## Slideshow Component
   * =============================================================
   */

  class SlideshowComponent extends CustomHTMLElement {
    connectedCallback() {
      this.slideshow = this.querySelector('[id^="Slideshow-"]');
      this.slideshowItems = this.querySelectorAll('[id^="Slide-"]');
      this.slideshowImages = this.querySelectorAll('.slideshow__image');
      this.slideshowFirstItemNode = this.slideshow.querySelector('.slideshow__slide');

      this.prevNextButtonsElements = Array.from(this.querySelectorAll("prev-next-buttons"));

      this.dotsButtonsElements = Array.from(this.querySelectorAll('.dots-nav__item'));

      // Defensive code
      if (!this.slideshow || this.slideshowItems.length < 2) {
        return;
      } else {
        this.currentPage = 1;
      }

      // Shopify Theme Editor 
      if (Shopify.designMode) {
        this.addEventListener("shopify:block:deselect", (event) => {
          if (this.slideshow.getAttribute('data-autoplay') === 'true') {
            this.play();
          }
        });

        this.addEventListener("shopify:block:select", (event) => {
          const blockSelectedIsSlide = event.target.classList.contains('slideshow__slide');
          if (this.slideshow.getAttribute('data-autoplay') === 'true') {
            this.pause();
          }
          this.slideshow.scrollTo({
            left: event.target.offsetLeft
          });

        });
      }

      // Init
      this.initPages();

      if (window.ResizeObserver) {
        // Resize event listener on the slideshow
        const resizeObserver = new ResizeObserver(entries => {
          this.initPages();
        });

        resizeObserver.observe(this.slideshow);
      }

      // A scroll event listener. This will update any associated elements such as buttons or page dots.
      this.slideshow.addEventListener("scroll", this.update.bind(this));

      // Event listeners for arrow buttons
      this.addEventListener("prev-next:prev", this.prev.bind(this));
      this.addEventListener("prev-next:next", this.next.bind(this));

      this.dotsButtonsElements.forEach(link => link.addEventListener('click', this.linkToSlide.bind(this)));

      // Autoplay
      if (this.slideshow.getAttribute('data-autoplay') === 'true') {
        this.setAutoplay();
      }

      if (this.hasAttribute("parallax") && !MediaFeatures.prefersReducedMotion()) {
        this._hasPendingRaF = false;
        this._onScrollListener = this._onScroll.bind(this);

        window.addEventListener("scroll", this._onScrollListener);
      }
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      if (this._onScrollListener) {
        window.removeEventListener("scroll", this._onScrollListener);
      }
    }

    _onScroll() {
      if (this._hasPendingRaF) {
        return;
      }

      this._hasPendingRaF = true;
      requestAnimationFrame(() => {
        const boundingRect = this.getBoundingClientRect(), speedFactor = 3; 

        // Use image wrapper as we may have an overlay
        const imageElement = this.querySelector(".slideshow__image-wrapper");

        const boundingRectBottom = boundingRect.bottom;
        const boundingRectHeight = boundingRect.height;
        const stickyHeaderOffset = getStickyHeaderOffset();

        if (imageElement) {
          this.slideshowImages.forEach(slideshowImage => {
            slideshowImage.style.transform = `translateY(${100 - Math.max(1 - (1 - Math.min(boundingRectBottom / (boundingRectHeight + stickyHeaderOffset), 1)), 0) * 100}px)`;
          });
        }
        
        this._hasPendingRaF = false;
      });
    }

    initPages() {
      // Defensive code to ensure slideshow items are larger than 0 width
      this.slideshowItemsToShow = Array.from(this.slideshowItems).filter(element => element.clientWidth > 0);
      
      // Get the last slideshow item. Helps with future calculations and determining if 'next' buttons are disabled
      this.slideshowLastItem = this.slideshowItemsToShow[this.slideshowItemsToShow.length - 1];

      // Sees how many slides are shown in a single view. You may have a product list with four items etc.
      this.slidesPerPage = Math.floor(this.slideshow.clientWidth / this.slideshowItemsToShow[0].clientWidth);

      // Gets how many "pages" there would be. Eg if we have 2 full width slides, then there are 2 pages.
      this.totalPages = this.slideshowItemsToShow.length - this.slidesPerPage + 1;

      this.update();
    }

    update() {
      const previousPage = this.currentPage;

      // Get currentPage
      this.currentPage = Math.round(this.slideshow.scrollLeft / this.slideshowLastItem.clientWidth) + 1;

      // Dispatch custom event if slide has changed
      if (this.currentPage != previousPage) {
        this.dispatchEvent(new CustomEvent('slide:changed', { detail: {
          currentPage: this.currentPage,
          currentElement: this.slideshowItemsToShow[this.currentPage - 1]
        }}));
      }

      if (!this.dotsButtonsElements.length) {
        return;
      }

      this.dotsButtonsElements.forEach(link => {
        link.removeAttribute('aria-current');
      });

      this.dotsButtonsElements[this.currentPage - 1].setAttribute('aria-current', true);
    }

    setAutoplay() {
      this.autoplaySpeed = this.slideshow.dataset.speed * 1000;

      // Add event listeners to start or stop autoplay based on focus
      this.addEventListener('mouseover', this.focusInHandling.bind(this));
      this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
      this.addEventListener('focusin', this.focusInHandling.bind(this));
      this.addEventListener('focusout', this.focusOutHandling.bind(this));

      this.play();
    }

    play() {
      this.slideshow.setAttribute('aria-live', 'off');
      clearInterval(this.autoplay);
      this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
    }

    pause() {
      this.slideshow.setAttribute('aria-live', 'polite');
      clearInterval(this.autoplay);
    }

    autoRotateSlides() {
      const slideScrollPosition = this.currentPage === this.slideshowItems.length ? 0 : this.slideshow.scrollLeft + this.slideshow.querySelector('.slideshow__slide').clientWidth;

      this.slideshow.scrollTo({
        left: slideScrollPosition
      });
    }

    focusInHandling(event) {
      this.pause();
    }

    focusOutHandling(event) {
      this.play();
    }

    prev(event) {
      event.preventDefault();

      this.slideScrollPosition = this.slideshow.scrollLeft - (this.slideshowLastItem.clientWidth);

      const isFirstSlide = this.currentPage === 1;

      // We have a looping slideshow so on the first slide, the "previous" button will take users to the last slide
      if (isFirstSlide) {
        this.slideScrollPosition = this.slideshow.scrollLeft + this.slideshowFirstItemNode.clientWidth * this.slideshowItemsToShow.length;
      }

      this.slideshow.scrollTo({
        left: this.slideScrollPosition
      });
      
    }

    next(event) {
      event.preventDefault();

      this.slideScrollPosition = this.slideshow.scrollLeft + (this.slideshowLastItem.clientWidth);

      const isLastSlide = this.currentPage === this.slideshowItemsToShow.length;

      // We have a looping slideshow so on the last slide, the "next" button will take users to the first slide
      if (isLastSlide) {
        this.slideScrollPosition = 0;
      }

      this.slideshow.scrollTo({
        left: this.slideScrollPosition
      });
    }

    linkToSlide(event) {
      event.preventDefault();
      const slideScrollPosition = this.slideshow.scrollLeft + this.slideshowFirstItemNode.clientWidth * (this.dotsButtonsElements.indexOf(event.currentTarget) + 1 - this.currentPage);

      this.slideshow.scrollTo({
        left: slideScrollPosition
      });
    }
  }

  window.customElements.define("slideshow-component", SlideshowComponent);










  /**
   * =============================================================
   * ## Hero Image
   * =============================================================
   */

  var HeroImage = class extends CustomHTMLElement {
    connectedCallback() {
      if (this.hasAttribute("parallax") && !MediaFeatures.prefersReducedMotion()) {
        this._hasPendingRaF = false;
        this._onScrollListener = this._onScroll.bind(this);

        window.addEventListener("scroll", this._onScrollListener);
      }
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      if (this._onScrollListener) {
        window.removeEventListener("scroll", this._onScrollListener);
      }
    }

    _onScroll() {
      if (this._hasPendingRaF) {
        return;
      }

      this._hasPendingRaF = true;
      requestAnimationFrame(() => {
        const boundingRect = this.getBoundingClientRect(); 
        const speedFactor = 3; 

        // Use image wrapper as we may have an overlay
        const imageElement = this.querySelector(".hero__image-wrapper");

        const boundingRectBottom = boundingRect.bottom; 
        const boundingRectHeight = boundingRect.height; 
        const stickyHeaderOffset = getStickyHeaderOffset();

        if (imageElement) {
          imageElement.style.transform = `translateY(${100 - Math.max(1 - (1 - Math.min(boundingRectBottom / (boundingRectHeight + stickyHeaderOffset), 1)), 0) * 100}px)`;
        }

        this._hasPendingRaF = false;
      });
    }
  };

  window.customElements.define("hero-image", HeroImage);










  /**
   * =============================================================
   * ## DOM READY
   * =============================================================
   */

  (() => {
    new InputBindingManager();
  })();


  // PAGE TRANSITIONS
  (() => {
    window.addEventListener('beforeunload', () => {
      document.body.classList.add('unloading');
    });
    window.addEventListener('DOMContentLoaded', () => {
      document.body.classList.add('loaded');
    });
    window.addEventListener('pageshow', (event) => {
      // Removes unload class when returning to page via history
      if (event.persisted) {
        document.body.classList.remove('unloading');
      }
    });
  })();

  (() => {
    let previousClientWidth = window.visualViewport ? window.visualViewport.width : document.documentElement.clientWidth;

    let setViewportProperty = () => {
      const clientWidth = window.visualViewport ? window.visualViewport.width : document.documentElement.clientWidth, clientHeight = window.visualViewport ? window.visualViewport.height : document.documentElement.clientHeight;

      if (clientWidth === previousClientWidth) {
        return;
      }

      requestAnimationFrame(() => {
        document.documentElement.style.setProperty("--window-height", clientHeight + "px");
        previousClientWidth = clientWidth;
      });
    };

    setViewportProperty();

    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", setViewportProperty);
    } else {
      window.addEventListener("resize", setViewportProperty);
    }
  })();


  (() => {
    document.querySelectorAll(".rte table").forEach((table) => {
      table.outerHTML = '<div class="table-wrapper">' + table.outerHTML + "</div>";
    });

    document.querySelectorAll(".rte iframe").forEach((iframe) => {
      if (iframe.src.indexOf("youtube") !== -1 || iframe.src.indexOf("youtu.be") !== -1 || iframe.src.indexOf("vimeo") !== -1) {
        iframe.outerHTML = '<div class="video-wrapper">' + iframe.outerHTML + "</div>";
      }
    });
  })();


  (() => {
    document.addEventListener("keyup", function(event) {
      if (event.key === "Tab") {
        document.body.classList.remove("no-focus-outline");
        document.body.classList.add("focus-outline");
      }
    });
  })();